import React, { useEffect, useState, useRef, useMemo } from "react";
import uitoolkit from "@zoom/videosdk-ui-toolkit";
import "@zoom/videosdk-ui-toolkit/dist/videosdk-ui-toolkit.css";
import "./App.css";
import PreviewPage from "./PreviewPage";

const REZRATE_VIDEO_WINDOW_TARGET = "rezrate-video";
const REZRATE_VIDEO_PRIMARY_WINDOW_STORAGE_KEY = "rezrate_video_primary_window";
const REZRATE_VIDEO_WINDOW_HANDOFF_STORAGE_KEY = "rezrate_video_window_handoff";
const REZRATE_VIDEO_WINDOW_HANDOFF_SUPPRESS_SESSION_KEY =
  "rezrate_video_window_handoff_suppress";
const REZRATE_VIDEO_WINDOW_TAB_ID_SESSION_KEY = "rezrate_video_window_tab_id";
const REZRATE_VIDEO_WINDOW_HANDOFF_CHANNEL = "rezrate-video-window-handoff";
const REZRATE_VIDEO_SUPPRESS_BEFOREUNLOAD_PROMPT_SESSION_KEY =
  "rezrate_video_suppress_beforeunload_prompt";
const REZRATE_VIDEO_WINDOW_HANDOFF_ACK_TIMEOUT_MS = 4000;
const REZRATE_VIDEO_WINDOW_HANDOFF_POLL_INTERVAL_MS = 500;
const REZRATE_VIDEO_WINDOW_HANDOFF_MESSAGE_TTL_MS = 30 * 1000;
const REZRATE_VIDEO_PRIMARY_WINDOW_TTL_MS = 12 * 60 * 60 * 1000;
const REZRATE_VIDEO_PRIMARY_WINDOW_HEARTBEAT_MS = 2000;
const REZRATE_VIDEO_SUPPRESS_BEFOREUNLOAD_PROMPT_TTL_MS = 2 * 60 * 1000;

const getSafeUrlForWindowLog = (rawUrl = "") => {
  const raw = `${rawUrl || ""}`;
  if (!raw) {
    return { safeUrl: "", hasQuery: false, hasHash: false };
  }

  const hasQuery = raw.includes("?");
  const hasHash = raw.includes("#");

  try {
    const parsed = new URL(raw, window.location.origin);
    return {
      safeUrl: `${parsed.origin}${parsed.pathname}`,
      hasQuery,
      hasHash,
    };
  } catch (e) {
    return {
      safeUrl: raw.split("?")[0].split("#")[0],
      hasQuery,
      hasHash,
    };
  }
};

const logVideoWindowEvent = (event, details = {}) => {
  try {
    console.info("[RezRATE][video-window]", event, details);
  } catch (e) {
    // ignore
  }
};

const createVideoWindowTabId = () =>
  `rezrate-video-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const getSafePathForWindowLog = () => {
  try {
    return `${window.location.origin}${window.location.pathname}`;
  } catch (e) {
    return "";
  }
};

const isTopLevelVideoWindowContext = () => {
  try {
    return window.top === window.self;
  } catch (e) {
    return true;
  }
};

const ensureNamedVideoWindow = (reason = "unknown") => {
  try {
    if (typeof window === "undefined") return;
    if (!isTopLevelVideoWindowContext()) {
      return;
    }
    const previousWindowName = window.name || "";
    if (previousWindowName !== REZRATE_VIDEO_WINDOW_TARGET) {
      window.name = REZRATE_VIDEO_WINDOW_TARGET;
    }
    const currentWindowName = window.name || "";
    logVideoWindowEvent("name.ensure", {
      reason,
      previousWindowName,
      currentWindowName,
      safePath: getSafePathForWindowLog(),
    });
  } catch (e) {
    // ignore
  }
};

try {
  if (typeof window !== "undefined") {
    ensureNamedVideoWindow("module-init");
  }
} catch (e) {
  // ignore
}

const formatErrorMessage = (error, fallback = "Unknown error") => {
  if (!error) return fallback;
  if (typeof error === "string") return error;
  if (typeof error?.message === "string" && error.message.trim() !== "") {
    return error.message;
  }
  try {
    const serialized = JSON.stringify(error);
    if (serialized && serialized !== "{}") {
      return serialized;
    }
  } catch (e) {
    // ignore JSON serialization errors
  }
  try {
    return String(error);
  } catch (e) {
    return fallback;
  }
};

const isExpectedSessionTerminationError = (error) => {
  const type = `${error?.type || ""}`.toLowerCase();
  const reason = `${error?.reason || ""}`.toLowerCase();
  const errorCode = `${error?.errorCode || ""}`.toLowerCase();
  const message = formatErrorMessage(error, "").toLowerCase();
  const combined = `${type} ${reason} ${errorCode} ${message}`;
  return (
    combined.includes("duplicated operation") ||
    combined.includes("improper session state") ||
    combined.includes("not in a session") ||
    combined.includes("not in session") ||
    combined.includes("session closed") ||
    combined.includes("closed") ||
    combined.includes("insufficient_privileges") ||
    combined.includes("insufficient privileges") ||
    combined.includes("only host can do the operation") ||
    combined.includes("5007")
  );
};

const isKnownToolkitSnackbarError = (error) => {
  const message = formatErrorMessage(error, "").toLowerCase();
  return message.includes("enqueuesnackbar called with invalid argument");
};

const suppressGlobalErrorEvent = (event) => {
  try {
    if (event && typeof event.preventDefault === "function") {
      event.preventDefault();
    }
    if (event && typeof event.stopImmediatePropagation === "function") {
      event.stopImmediatePropagation();
    }
    if (event && typeof event.stopPropagation === "function") {
      event.stopPropagation();
    }
  } catch (e) {
    // ignore
  }
};

const isVirtualBackgroundFailure = (error) => {
  try {
    if (!error) return false;
    if (
      error?.type === "VIDEO_BACKGROUND_FAILED" ||
      error?.errorCode === 6104 ||
      error?.subCode === 6114
    ) {
      return true;
    }
    const message = formatErrorMessage(error, "").toLowerCase();
    return (
      message.includes("virtual background") ||
      message.includes("background model") ||
      message.includes("vb-resource") ||
      message.includes("tf.min.js")
    );
  } catch (e) {
    return false;
  }
};

const isWakeLockPermissionsPolicyError = (error) => {
  try {
    const message = formatErrorMessage(error, "").toLowerCase();
    return (
      message.includes("failed to execute 'request' on 'wakelock'") ||
      message.includes('failed to execute "request" on "wakelock"') ||
      message.includes("screen wake lock features is disallowed by permissions policy") ||
      (message.includes("wake lock") && message.includes("permissions policy"))
    );
  } catch (e) {
    return false;
  }
};

function MeetingApp() {
  const [error, setError] = useState(null);
  const [handoffNotice, setHandoffNotice] = useState("");
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [isHost, setIsHost] = useState(false); // Track if user is host (role=1)
  const inactivityTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const joinConfigRef = useRef(null);
  const mirrorInitIntervalRef = useRef(null);
  const mirrorUiSyncIntervalRef = useRef(null);
  const mirrorUiObserverRef = useRef(null);
  const mirrorUiSyncStartedAtRef = useRef(0);
  const mirrorUiAutoOpenTriedRef = useRef(false);
  const mirrorUserUpdatedHandlerRef = useRef(null);
  const mirrorUiClickHandlerRef = useRef(null);
  const audioErrorPatchIntervalRef = useRef(null);
  const mirrorPrefRef = useRef(true);
  const mediaPrefsRef = useRef({
    cameraId: "",
    microphoneId: "",
    speakerId: "",
    blurBackground: false,
    audioOn: null,
    videoOn: null,
  });
  const serverOffsetMsRef = useRef(0);
  const endTimerRef = useRef(null);
  const endCountdownIntervalRef = useRef(null);
  const endedConsecutiveRef = useRef(0);
  const [endsInSeconds, setEndsInSeconds] = useState(null);
  const [displayTopic, setDisplayTopic] = useState("");
  const mirrorStorageKeyRef = useRef(null);
  const mediaPrefsStorageKeyRef = useRef(null);
  const suppressBeforeUnloadPromptRef = useRef(false);
  const mediaDevicesByLabelRef = useRef({
    cameras: new Map(),
    microphones: new Map(),
    speakers: new Map(),
  });
  const mediaPrefsApplyStateRef = useRef({
    lastAttemptAt: 0,
    lastAppliedSnapshot: null,
  });
  // Optional. If not set, we do not auto-close sessions based on inactivity.
  // This should eventually be replaced by a schedule-aware end-of-block timer (incl. passing period).
  const INACTIVITY_TIMEOUT = (() => {
    const raw = process.env.REACT_APP_INACTIVITY_TIMEOUT_MS;
    if (!raw) return 0;
    const parsed = parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  })();
  const authEndpoint = process.env.REACT_APP_ZOOM_API;
  const [showSessionEndedPopup, setShowSessionEndedPopup] = useState(false);
  const sessionContainerRef = useRef(null);
  const sessionJoinedAtRef = useRef(0);
  const isSessionActiveRef = useRef(false);
  const lastHandledJoinIntentKeyRef = useRef("");
  const joinIntentChangeInFlightRef = useRef(false);
  const lastObservedJoinHrefRef = useRef("");
  const videoWindowTabIdRef = useRef("");
  const primaryWindowHeartbeatRef = useRef(null);
  const handoffStoragePollIntervalRef = useRef(null);
  const handoffLastSeenNonceRef = useRef("");
  const handoffChannelRef = useRef(null);
  const pendingHandoffRequestRef = useRef(null);
  const handledHandoffRequestIdsRef = useRef(new Set());

  const virtualBackgroundSupported = useMemo(() => {
    try {
      const canvas = document.createElement("canvas");
      return !!(canvas.getContext("webgl2") || canvas.getContext("webgl"));
    } catch (e) {
      return false;
    }
  }, []);

  const DEFAULT_FEATURES_OPTIONS = {
    preview: {
      enable: true,
      isAllowModifyName: false,
    },
    video: {
      enable: true,
      originalRatio: true,
      // Some Windows environments (or hardened browsers) disable WebGL; attempting to initialize
      // virtual background can throw and/or surface misleading permission errors.
      virtualBackground: virtualBackgroundSupported,
    },
    audio: {
      enable: true,
      backgroundNoiseSuppression: true,
      originalSound: false,
    },
    share: {
      enable: true,
    },
    chat: {
      enable: false,
      enableEmoji: false,
    },
    users: {
      enable: true,
    },
    settings: {
      enable: true,
    },
    theme: {
      enable: true,
      defaultTheme: "light",
    },
    subsession: {
      enable: false,
    },
    leave: {
      enable: false,
    },
    footer: {
      enable: true,
    },
    header: {
      enable: false,
    },
    feedback: {
      enable: false,
    },
  };

  const getApiBase = () => {
    const raw = `${authEndpoint || ""}`.replace(/\/+$/, "");
    if (!raw) return "";
    return raw.endsWith("/zoom") ? raw.slice(0, -5) : raw;
  };

  const getOrCreateVideoWindowTabId = () => {
    if (videoWindowTabIdRef.current) {
      return videoWindowTabIdRef.current;
    }

    let existing = "";
    try {
      existing =
        window.sessionStorage.getItem(REZRATE_VIDEO_WINDOW_TAB_ID_SESSION_KEY) || "";
    } catch (e) {
      existing = "";
    }

    const tabId = `${existing || ""}`.trim() || createVideoWindowTabId();
    videoWindowTabIdRef.current = tabId;

    try {
      window.sessionStorage.setItem(REZRATE_VIDEO_WINDOW_TAB_ID_SESSION_KEY, tabId);
    } catch (e) {
      // ignore
    }

    logVideoWindowEvent("tab.identity", {
      tabId,
      reusedExistingId: !!existing,
      safePath: getSafePathForWindowLog(),
    });

    return tabId;
  };

  const readPrimaryVideoWindow = () => {
    if (!isTopLevelVideoWindowContext()) {
      return null;
    }
    try {
      const raw = window.localStorage.getItem(
        REZRATE_VIDEO_PRIMARY_WINDOW_STORAGE_KEY
      );
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const tabId = `${parsed?.tabId || ""}`.trim();
      const updatedAt = Number(parsed?.updatedAt);
      if (!tabId || !Number.isFinite(updatedAt) || updatedAt <= 0) {
        return null;
      }
      return { tabId, updatedAt };
    } catch (e) {
      return null;
    }
  };

  const isFreshPrimaryVideoWindow = (entry) => {
    if (!entry) return false;
    return Date.now() - Number(entry.updatedAt || 0) <= REZRATE_VIDEO_PRIMARY_WINDOW_TTL_MS;
  };

  const writePrimaryVideoWindow = (tabId, reason = "unknown") => {
    if (!isTopLevelVideoWindowContext()) {
      return null;
    }
    const entry = {
      tabId,
      updatedAt: Date.now(),
    };
    try {
      window.localStorage.setItem(
        REZRATE_VIDEO_PRIMARY_WINDOW_STORAGE_KEY,
        JSON.stringify(entry)
      );
    } catch (e) {
      // ignore
    }
    logVideoWindowEvent("handoff.primary.claimed", {
      reason,
      tabId,
      safePath: getSafePathForWindowLog(),
    });
    return entry;
  };

  const claimPrimaryVideoWindow = (reason = "unknown", options = {}) => {
    if (!isTopLevelVideoWindowContext()) {
      return false;
    }
    const { force = false } = options || {};
    const tabId = getOrCreateVideoWindowTabId();
    const currentPrimary = readPrimaryVideoWindow();

    if (
      !force &&
      currentPrimary &&
      isFreshPrimaryVideoWindow(currentPrimary) &&
      currentPrimary.tabId !== tabId
    ) {
      return false;
    }

    writePrimaryVideoWindow(tabId, reason);
    return true;
  };

  const refreshPrimaryVideoWindowHeartbeat = () => {
    if (!isTopLevelVideoWindowContext()) {
      return;
    }
    const tabId = getOrCreateVideoWindowTabId();
    const currentPrimary = readPrimaryVideoWindow();

    if (!currentPrimary) {
      writePrimaryVideoWindow(tabId, "heartbeat.claim");
      return;
    }

    if (currentPrimary.tabId !== tabId) {
      return;
    }

    try {
      window.localStorage.setItem(
        REZRATE_VIDEO_PRIMARY_WINDOW_STORAGE_KEY,
        JSON.stringify({
          tabId,
          updatedAt: Date.now(),
        })
      );
    } catch (e) {
      // ignore
    }
  };

  const clearPrimaryVideoWindowClaim = (reason = "unknown") => {
    if (!isTopLevelVideoWindowContext()) {
      return;
    }
    const tabId = getOrCreateVideoWindowTabId();
    const currentPrimary = readPrimaryVideoWindow();

    if (!currentPrimary || currentPrimary.tabId !== tabId) {
      return;
    }

    try {
      window.localStorage.removeItem(REZRATE_VIDEO_PRIMARY_WINDOW_STORAGE_KEY);
    } catch (e) {
      // ignore
    }

    logVideoWindowEvent("handoff.primary.cleared", {
      reason,
      tabId,
      safePath: getSafePathForWindowLog(),
    });
  };

  const clearPendingHandoffRequest = () => {
    const pending = pendingHandoffRequestRef.current;
    if (!pending) return;
    if (pending.timeoutId) {
      window.clearTimeout(pending.timeoutId);
    }
    pendingHandoffRequestRef.current = null;
  };

  const postVideoWindowHandoffMessage = (message) => {
    const payload = {
      ...message,
      emittedAt: Date.now(),
      nonce: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    };

    try {
      if (handoffChannelRef.current) {
        handoffChannelRef.current.postMessage(payload);
      }
    } catch (e) {
      // ignore
    }

    try {
      window.localStorage.setItem(
        REZRATE_VIDEO_WINDOW_HANDOFF_STORAGE_KEY,
        JSON.stringify(payload)
      );
    } catch (e) {
      // ignore
    }

    return payload;
  };

  const storeHandoffSuppressTarget = (targetHref, requestId) => {
    try {
      window.sessionStorage.setItem(
        REZRATE_VIDEO_WINDOW_HANDOFF_SUPPRESS_SESSION_KEY,
        JSON.stringify({
          requestId,
          targetHref,
          createdAt: Date.now(),
        })
      );
    } catch (e) {
      // ignore
    }
  };

  const markSuppressBeforeUnloadPrompt = (reason = "unknown") => {
    suppressBeforeUnloadPromptRef.current = true;
    try {
      window.sessionStorage.setItem(
        REZRATE_VIDEO_SUPPRESS_BEFOREUNLOAD_PROMPT_SESSION_KEY,
        JSON.stringify({
          reason,
          createdAt: Date.now(),
        })
      );
    } catch (e) {
      // ignore
    }
  };

  const consumeSuppressBeforeUnloadPrompt = () => {
    if (suppressBeforeUnloadPromptRef.current) {
      suppressBeforeUnloadPromptRef.current = false;
      try {
        window.sessionStorage.removeItem(
          REZRATE_VIDEO_SUPPRESS_BEFOREUNLOAD_PROMPT_SESSION_KEY
        );
      } catch (e) {
        // ignore
      }
      return true;
    }

    try {
      const raw = window.sessionStorage.getItem(
        REZRATE_VIDEO_SUPPRESS_BEFOREUNLOAD_PROMPT_SESSION_KEY
      );
      if (!raw) return false;

      const parsed = JSON.parse(raw);
      const createdAt = Number(parsed?.createdAt || 0);
      const isFresh =
        Number.isFinite(createdAt) &&
        Date.now() - createdAt <= REZRATE_VIDEO_SUPPRESS_BEFOREUNLOAD_PROMPT_TTL_MS;

      window.sessionStorage.removeItem(
        REZRATE_VIDEO_SUPPRESS_BEFOREUNLOAD_PROMPT_SESSION_KEY
      );
      return isFresh;
    } catch (e) {
      return false;
    }
  };

  const consumeMatchingHandoffSuppressTarget = () => {
    try {
      const raw = window.sessionStorage.getItem(
        REZRATE_VIDEO_WINDOW_HANDOFF_SUPPRESS_SESSION_KEY
      );
      if (!raw) return false;

      const parsed = JSON.parse(raw);
      const targetHref = `${parsed?.targetHref || ""}`;
      const createdAt = Number(parsed?.createdAt || 0);
      const isExpired = !Number.isFinite(createdAt) || Date.now() - createdAt > 60000;
      const matches = targetHref && targetHref === window.location.href;

      if (isExpired || matches) {
        window.sessionStorage.removeItem(
          REZRATE_VIDEO_WINDOW_HANDOFF_SUPPRESS_SESSION_KEY
        );
      }

      if (matches) {
        const { safeUrl, hasQuery, hasHash } = getSafeUrlForWindowLog(targetHref);
        logVideoWindowEvent("handoff.suppressed", {
          requestId: parsed?.requestId || "",
          safeUrl,
          hasQuery,
          hasHash,
        });
        return true;
      }
    } catch (e) {
      // ignore
    }

    return false;
  };

  const closeDuplicateWindowAfterHandoff = () => {
    setHandoffNotice("Meeting moved to your existing video window.");
    logVideoWindowEvent("handoff.self.close.attempt", {
      tabId: getOrCreateVideoWindowTabId(),
      safePath: getSafePathForWindowLog(),
    });
    markSuppressBeforeUnloadPrompt("handoff.self-close");
    try {
      window.close();
    } catch (e) {
      // ignore
    }
  };

  const handleIncomingVideoWindowHandoffMessage = (message, source = "unknown") => {
    if (!message || typeof message !== "object") return;

    const messageType = `${message.type || ""}`.trim();
    const sourceTabId = `${message.sourceTabId || ""}`.trim();
    const targetTabId = `${message.targetTabId || ""}`.trim();
    const requestId = `${message.requestId || ""}`.trim();
    const selfTabId = getOrCreateVideoWindowTabId();

    if (!messageType || sourceTabId === selfTabId) {
      return;
    }

    if (messageType === "handoff-request") {
      const currentPrimary = readPrimaryVideoWindow();
      const targetedToSelf =
        (targetTabId && targetTabId === selfTabId) ||
        (!targetTabId &&
          currentPrimary &&
          isFreshPrimaryVideoWindow(currentPrimary) &&
          currentPrimary.tabId === selfTabId);

      if (!targetedToSelf || !requestId) {
        return;
      }

      if (handledHandoffRequestIdsRef.current.has(requestId)) {
        return;
      }
      handledHandoffRequestIdsRef.current.add(requestId);

      const targetHref = `${message.targetHref || ""}`;
      const { safeUrl, hasQuery, hasHash } = getSafeUrlForWindowLog(targetHref);

      logVideoWindowEvent("handoff.request.accepted", {
        source,
        requestId,
        sourceTabId,
        targetTabId: selfTabId,
        safeUrl,
        hasQuery,
        hasHash,
      });

      storeHandoffSuppressTarget(targetHref, requestId);
      claimPrimaryVideoWindow("handoff.accepted", { force: true });
      markSuppressBeforeUnloadPrompt("handoff.accepted");

      postVideoWindowHandoffMessage({
        type: "handoff-ack",
        requestId,
        sourceTabId: selfTabId,
        targetTabId: sourceTabId,
        targetHref,
      });

      try {
        window.location.replace(targetHref);
      } catch (e) {
        window.location.href = targetHref;
      }
      return;
    }

    if (messageType === "handoff-ack") {
      if (!requestId || targetTabId !== selfTabId) {
        return;
      }

      const pending = pendingHandoffRequestRef.current;
      if (!pending || pending.requestId !== requestId) {
        return;
      }

      const { safeUrl, hasQuery, hasHash } = getSafeUrlForWindowLog(
        message.targetHref || window.location.href
      );

      logVideoWindowEvent("handoff.ack.received", {
        source,
        requestId,
        responderTabId: sourceTabId,
        safeUrl,
        hasQuery,
        hasHash,
      });

      clearPendingHandoffRequest();
      pending.resolve(true);
    }
  };

  const requestExistingVideoWindowTakeover = async () => {
    if (!isTopLevelVideoWindowContext()) {
      return false;
    }

    const currentJoinParams = getJoinParams();
    const hasJoinIntent = !!(
      currentJoinParams?.sessionName ||
      currentJoinParams?.jwt ||
      currentJoinParams?.userId ||
      currentJoinParams?.rt ||
      currentJoinParams?.guestToken
    );

    if (!hasJoinIntent) {
      return false;
    }

    if (consumeMatchingHandoffSuppressTarget()) {
      return false;
    }

    const tabId = getOrCreateVideoWindowTabId();
    const currentPrimary = readPrimaryVideoWindow();

    if (
      !currentPrimary ||
      !isFreshPrimaryVideoWindow(currentPrimary) ||
      currentPrimary.tabId === tabId
    ) {
      claimPrimaryVideoWindow("handoff.no-existing-primary");
      return false;
    }

    const requestId = `${tabId}:${Date.now()}`;
    const targetHref = window.location.href;
    const { safeUrl, hasQuery, hasHash } = getSafeUrlForWindowLog(targetHref);

    logVideoWindowEvent("handoff.request.sent", {
      requestId,
      tabId,
      targetTabId: currentPrimary.tabId,
      safeUrl,
      hasQuery,
      hasHash,
    });

    // Publish the request so the already-open video tab can acknowledge and self-navigate.
    postVideoWindowHandoffMessage({
      type: "handoff-request",
      requestId,
      sourceTabId: tabId,
      targetTabId: currentPrimary.tabId,
      targetHref,
    });

    const handedOff = await new Promise((resolve) => {
      const timeoutId = window.setTimeout(() => {
        if (
          pendingHandoffRequestRef.current &&
          pendingHandoffRequestRef.current.requestId === requestId
        ) {
          pendingHandoffRequestRef.current = null;
        }

        logVideoWindowEvent("handoff.request.timeout", {
          requestId,
          tabId,
          targetTabId: currentPrimary.tabId,
          safeUrl,
          hasQuery,
          hasHash,
        });
        claimPrimaryVideoWindow("handoff.timeout", { force: true });
        resolve(false);
      }, REZRATE_VIDEO_WINDOW_HANDOFF_ACK_TIMEOUT_MS);

      pendingHandoffRequestRef.current = {
        requestId,
        timeoutId,
        resolve,
      };
    });

    if (!handedOff) {
      return false;
    }

    closeDuplicateWindowAfterHandoff();
    return true;
  };

  const leaveSessionForAllSafe = ({
    endForAll = false,
    logUnexpected = true,
  } = {}) => {
    try {
      if (!uitoolkit.client || typeof uitoolkit.client.leave !== "function") {
        return Promise.resolve(false);
      }
      return Promise.resolve(uitoolkit.client.leave(!!endForAll))
        .then(() => true)
        .catch((error) => {
          if (!isExpectedSessionTerminationError(error) && logUnexpected) {
            console.error(
              `leave(${endForAll ? "true" : "false"}) failed:`,
              formatErrorMessage(error)
            );
          }
          return false;
        });
    } catch (error) {
      if (!isExpectedSessionTerminationError(error) && logUnexpected) {
        console.error(
          `leave(${endForAll ? "true" : "false"}) threw:`,
          formatErrorMessage(error)
        );
      }
      return Promise.resolve(false);
    }
  };

  const resolveDisplayTopic = (config = {}) => {
    const candidates = [
      config?.topic,
      config?.Topic,
      config?.meetingTopic,
      config?.MeetingTopic,
    ];
    for (const value of candidates) {
      const normalized = `${value || ""}`.trim();
      if (normalized) {
        return normalized;
      }
    }
    return undefined;
  };

  const resolveReadableMeetingTitle = (config = {}) => {
    const explicitTopic = resolveDisplayTopic(config);
    if (explicitTopic) return explicitTopic;

    const sessionName = `${config?.sessionName || ""}`.trim();
    if (!sessionName) return "RezRATE Meeting";
    if (/^rezrate_[a-z0-9]+$/i.test(sessionName)) {
      return "RezRATE Meeting";
    }
    return sessionName;
  };

  const stripUrlFragment = () => {
    try {
      if (window.location.hash) {
        window.history.replaceState(
          null,
          document.title,
          `${window.location.pathname}${window.location.search}`
        );
      }
    } catch (e) {
      // ignore
    }
  };

  const clearMirrorInitTimer = () => {
    if (mirrorInitIntervalRef.current) {
      window.clearInterval(mirrorInitIntervalRef.current);
      mirrorInitIntervalRef.current = null;
    }
  };

  const clearMirrorUiSyncTimer = () => {
    if (mirrorUiSyncIntervalRef.current) {
      window.clearInterval(mirrorUiSyncIntervalRef.current);
      mirrorUiSyncIntervalRef.current = null;
    }
  };

  const clearMirrorUiObserver = () => {
    if (mirrorUiObserverRef.current) {
      try {
        mirrorUiObserverRef.current.disconnect();
      } catch (e) {
        // ignore
      } finally {
        mirrorUiObserverRef.current = null;
      }
    }
  };

  const clearMirrorUserUpdatedListener = () => {
    const handler = mirrorUserUpdatedHandlerRef.current;
    if (!handler) return;
    try {
      const client = uitoolkit.client;
      if (client && typeof client.off === "function") {
        client.off("user-updated", handler);
      }
    } catch (e) {
      // ignore
    } finally {
      mirrorUserUpdatedHandlerRef.current = null;
    }
  };

  const clearMirrorUiClickListener = () => {
    try {
      const handler = mirrorUiClickHandlerRef.current;
      if (handler) {
        document.removeEventListener("click", handler, true);
        document.removeEventListener("change", handler, true);
      }
    } catch (e) {
      // ignore
    } finally {
      mirrorUiClickHandlerRef.current = null;
    }
  };

  const clearAudioErrorPatchTimer = () => {
    if (audioErrorPatchIntervalRef.current) {
      window.clearInterval(audioErrorPatchIntervalRef.current);
      audioErrorPatchIntervalRef.current = null;
    }
  };

  const showJoinFlow = () => {
    const joinFlow = document.getElementById("join-flow");
    if (!joinFlow) {
      return;
    }

    joinFlow.style.display = "flex";
    joinFlow.style.flexDirection = "column";
    joinFlow.style.alignItems = "center";
    joinFlow.style.justifyContent = "center";
    joinFlow.style.textAlign = "center";
    joinFlow.style.position = "absolute";
    joinFlow.style.inset = "0";
    joinFlow.style.width = "100%";
    joinFlow.style.height = "100%";
    joinFlow.style.minHeight = "0";
    joinFlow.style.padding = "24px";
    joinFlow.style.boxSizing = "border-box";
    joinFlow.style.background = "#ffffff";
    joinFlow.style.overflow = "hidden";

    const logo = joinFlow.querySelector("img");
    const heading = joinFlow.querySelector("h1");

    if (logo) {
      logo.style.display = "block";
      logo.style.margin = "0 auto 20px auto";
      logo.style.width = "200px";
      logo.style.float = "none";
      logo.style.alignSelf = "center";
    }

    if (heading) {
      heading.style.width = "100%";
      heading.style.textAlign = "center";
      heading.style.margin = "0 0 20px 0";
    }
  };

  const hideJoinFlow = () => {
    const joinFlow = document.getElementById("join-flow");
    if (!joinFlow) {
      return;
    }
    joinFlow.style.display = "none";
  };

  const patchToolkitStartAudioErrorMessage = () => {
    try {
      const client = uitoolkit.client;
      if (!client || typeof client.getMediaStream !== "function") {
        return false;
      }

      const mediaStream = client.getMediaStream();
      if (!mediaStream || typeof mediaStream.startAudio !== "function") {
        return false;
      }

      const patchStartAudioOnTarget = (target) => {
        if (!target || typeof target.startAudio !== "function") {
          return false;
        }
        if (target.__rezratePatchedStartAudio === true) {
          return true;
        }

        const originalStartAudio = target.startAudio;
        const wrappedStartAudio = async function (...args) {
          try {
            return await originalStartAudio.apply(this, args);
          } catch (error) {
            if (error && typeof error === "object") {
              const currentErrorMessage = `${error.errorMessage || ""}`.trim();
              if (!currentErrorMessage) {
                const fallback = formatErrorMessage(
                  error,
                  "Unable to start audio. Check microphone permissions and selected input."
                );
                try {
                  error.errorMessage = fallback;
                } catch (e) {
                  // ignore
                }
              }
            }
            throw error;
          }
        };

        try {
          target.startAudio = wrappedStartAudio;
          if (target.startAudio !== wrappedStartAudio) {
            return false;
          }
        } catch (e) {
          return false;
        }

        try {
          Object.defineProperty(target, "__rezratePatchedStartAudio", {
            configurable: true,
            enumerable: false,
            writable: true,
            value: true,
          });
        } catch (e) {
          target.__rezratePatchedStartAudio = true;
        }
        return true;
      };

      if (patchStartAudioOnTarget(mediaStream)) {
        return true;
      }

      const mediaStreamProto = Object.getPrototypeOf(mediaStream);
      return patchStartAudioOnTarget(mediaStreamProto);
    } catch (e) {
      return false;
    }
  };

  const ensureToolkitAudioErrorPatch = () => {
    clearAudioErrorPatchTimer();

    if (patchToolkitStartAudioErrorMessage()) {
      return;
    }

    let attempts = 0;
    const maxAttempts = 120; // ~30s at 250ms
    audioErrorPatchIntervalRef.current = window.setInterval(() => {
      attempts += 1;
      const patched = patchToolkitStartAudioErrorMessage();
      if (patched || attempts >= maxAttempts) {
        clearAudioErrorPatchTimer();
      }
    }, 250);
  };

  const normalizeControlText = (value) => {
    return `${value || ""}`
      .replace(/[\u200B-\u200D\uFEFF]/g, "")
      .replace(/[✓✔☑]/g, "")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();
  };

  const isButtonTextMatch = (button, labels = []) => {
    const normalizedButtonText = normalizeControlText(button?.textContent);
    if (!normalizedButtonText) return false;
    return labels.some(
      (label) => normalizedButtonText === normalizeControlText(label)
    );
  };

  const isMirrorMenuButton = (button) =>
    isButtonTextMatch(button, ["mirror my video"]);

  const isBlurMenuButton = (button) =>
    isButtonTextMatch(button, ["blur your background", "blur my background"]);

  const computeMirrorStorageKey = (config) => {
    const identity =
      config?.userIdentity ||
      config?.userId ||
      config?.userName ||
      null;
    if (identity) return `rezrate_mirror_video:${identity}`;
    const sessionName = config?.sessionName || joinConfigRef.current?.sessionName || "unknown";
    return `rezrate_mirror_video:guest:${sessionName}`;
  };

  const computeMediaPrefsStorageKey = (config) => {
    const identity =
      config?.userIdentity ||
      config?.userId ||
      config?.userName ||
      null;
    if (identity) return `rezrate_media_prefs:${identity}`;
    const sessionName = config?.sessionName || joinConfigRef.current?.sessionName || "unknown";
    return `rezrate_media_prefs:guest:${sessionName}`;
  };

  const readMirrorPreference = (storageKey) => {
    try {
      if (!storageKey) return null;
      const raw = window.localStorage.getItem(storageKey);
      if (raw === "1") return true;
      if (raw === "0") return false;
      return null;
    } catch (e) {
      return null;
    }
  };

  const writeMirrorPreference = (storageKey, value) => {
    try {
      if (!storageKey) return;
      window.localStorage.setItem(storageKey, value ? "1" : "0");
    } catch (e) {
      // ignore
    }
  };

  const readMediaPrefs = (storageKey) => {
    try {
      if (!storageKey) return null;
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return null;
      return parsed;
    } catch (e) {
      return null;
    }
  };

  const writeMediaPrefs = (storageKey, prefs) => {
    try {
      if (!storageKey) return;
      window.localStorage.setItem(storageKey, JSON.stringify(prefs || {}));
    } catch (e) {
      // ignore
    }
  };

  const isMirrorMenuButtonActive = (button) => {
    if (!button) return false;
    // In UI toolkit menu items, the active state is indicated by a leading check icon.
    // We avoid class-name checks because inactive items still include "hover:bg-theme-background".
    return !!button.querySelector("svg");
  };

  const findMenuButtonsByExactText = (lowerText) => {
    try {
      const labels = Array.isArray(lowerText) ? lowerText : [lowerText];
      const normalizedLabels = labels
        .map((label) => normalizeControlText(label))
        .filter(Boolean);
      if (normalizedLabels.length === 0) return [];
      return Array.from(document.querySelectorAll("button")).filter(
        (button) =>
          normalizedLabels.includes(normalizeControlText(button?.textContent))
      );
    } catch (e) {
      return [];
    }
  };

  const tryOpenVideoOptionsMenu = () => {
    try {
      const chevron = document.getElementById("uikit-footer-video-chevron");
      if (!chevron) return false;
      chevron.click();
      return true;
    } catch (e) {
      return false;
    }
  };

  const refreshMediaDeviceLabelMaps = async () => {
    const maps = {
      cameras: new Map(),
      microphones: new Map(),
      speakers: new Map(),
    };

    try {
      if (navigator.mediaDevices?.enumerateDevices) {
        const devices = await navigator.mediaDevices.enumerateDevices();
        devices.forEach((device) => {
          const label = normalizeControlText(device?.label);
          const id = `${device?.deviceId || ""}`.trim();
          if (!label || !id) return;
          if (device.kind === "videoinput") {
            maps.cameras.set(label, id);
          } else if (device.kind === "audioinput") {
            maps.microphones.set(label, id);
          } else if (device.kind === "audiooutput") {
            maps.speakers.set(label, id);
          }
        });
      }
    } catch (e) {
      // ignore
    }

    mediaDevicesByLabelRef.current = maps;
    return maps;
  };

  const isSystemDefaultDeviceId = (value) => {
    const normalized = normalizeControlText(value);
    return normalized === "default" || normalized === "communications";
  };

  const setNativeSelectValue = (select, nextValue) => {
    if (!select) return;
    try {
      const prototype = Object.getPrototypeOf(select);
      const descriptor = Object.getOwnPropertyDescriptor(prototype, "value");
      if (descriptor?.set) {
        descriptor.set.call(select, nextValue);
        return;
      }
    } catch (e) {
      // ignore and fall back to direct assignment
    }

    try {
      select.value = nextValue;
    } catch (e) {
      // ignore
    }
  };

  const dispatchNativeChange = (element) => {
    try {
      element.dispatchEvent(new Event("change", { bubbles: true }));
      return true;
    } catch (e) {
      return false;
    }
  };

  const getMediaStreamIfAvailable = () => {
    try {
      const client = uitoolkit.client;
      if (!client || typeof client.getMediaStream !== "function") return null;
      return client.getMediaStream();
    } catch (e) {
      return null;
    }
  };

  const safeSwitchToDevice = async ({ type, deviceId }) => {
    const id = `${deviceId || ""}`.trim();
    if (!id || isSystemDefaultDeviceId(id)) return true;
    const stream = getMediaStreamIfAvailable();
    if (!stream) return false;

    try {
      if (type === "camera") {
        if (typeof stream.switchCamera === "function") {
          await stream.switchCamera(id);
          return true;
        }
        if (typeof stream.setCamera === "function") {
          await stream.setCamera(id);
          return true;
        }
      } else if (type === "microphone") {
        if (typeof stream.switchMicrophone === "function") {
          await stream.switchMicrophone(id);
          return true;
        }
        if (typeof stream.setMicrophone === "function") {
          await stream.setMicrophone(id);
          return true;
        }
      } else if (type === "speaker") {
        if (typeof stream.switchSpeaker === "function") {
          await stream.switchSpeaker(id);
          return true;
        }
        if (typeof stream.setSpeaker === "function") {
          await stream.setSpeaker(id);
          return true;
        }
      }
    } catch (e) {
      return false;
    }

    // If the SDK/toolkit does not expose a switch method, treat as "system default".
    return true;
  };

  const resolveAvailableDeviceId = async ({ type, preferredId }) => {
    const preferred = `${preferredId || ""}`.trim();
    if (!preferred) return "";
    if (isSystemDefaultDeviceId(preferred)) {
      return "default";
    }

    try {
      if (navigator.mediaDevices?.enumerateDevices) {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const kind =
          type === "camera"
            ? "videoinput"
            : type === "microphone"
              ? "audioinput"
              : "audiooutput";
        const match = devices.find(
          (device) => device.kind === kind && `${device.deviceId || ""}`.trim() === preferred
        );
        if (match) return preferred;
      }
    } catch (e) {
      // ignore
    }

    return "";
  };

  const syncMirrorStateOnElement = (element, desired) => {
    if (!element || !(element instanceof HTMLElement)) return false;

    try {
      let currentlyMirrored = element.dataset?.mirrored === "1";
      const computedTransform = getComputedStyle(element).transform;

      if (typeof DOMMatrixReadOnly === "function") {
        const matrix = new DOMMatrixReadOnly(computedTransform);
        currentlyMirrored = currentlyMirrored || matrix.a < 0;
        if (currentlyMirrored !== desired) {
          element.style.transform = matrix.scale(-1, 1).toString();
        }
      } else if (currentlyMirrored !== desired) {
        element.style.transform = desired ? "scaleX(-1)" : "";
      }

      element.dataset.mirrored = desired ? "1" : "0";
      element.dataset.rezrateMirror = desired ? "1" : "0";
      return true;
    } catch (e) {
      try {
        element.style.transform = desired ? "scaleX(-1)" : "";
        element.dataset.mirrored = desired ? "1" : "0";
        element.dataset.rezrateMirror = desired ? "1" : "0";
        return true;
      } catch (innerError) {
        return false;
      }
    }
  };

  const applyMirrorPreferenceToRenderedVideo = () => {
    try {
      const desired = !!mirrorPrefRef.current;
      const candidates = Array.from(
        document.querySelectorAll(
          [
            '[id^="__ZOOM_SDK_SELF_VIDEO_"]',
            '[id^="__ZOOM_SDK_SELF_VIDEO_"] video',
            '[id^="__ZOOM_SDK_SELF_VIDEO_"] canvas',
            '[media-type="preview"]',
            '[media-type="preview"] video',
            '[media-type="preview"] canvas',
          ].join(",")
        )
      );

      let applied = false;
      candidates.forEach((element) => {
        applied = syncMirrorStateOnElement(element, desired) || applied;
      });
      return applied;
    } catch (e) {
      return false;
    }
  };

  const getToolkitDeviceSelectConfigs = () => [
    {
      prefKey: "speakerId",
      selectors: [
        "select#uikit-preview-speaker-select",
        "select#speaker",
        "select#audio-output-select",
      ],
    },
    {
      prefKey: "microphoneId",
      selectors: [
        "select#uikit-preview-mic-select",
        "select#settings-microphone",
        "select#audio-input-select",
      ],
    },
    {
      prefKey: "cameraId",
      selectors: [
        "select#uikit-preview-camera-select",
        "select#cameraSelect",
        "select#video-input-select",
      ],
    },
  ];

  const syncStoredDeviceSelectorsInToolkitUi = () => {
    try {
      const prefs = mediaPrefsRef.current || {};
      const selectConfigs = getToolkitDeviceSelectConfigs();
      let sawControl = false;
      let allMatched = true;

      selectConfigs.forEach(({ prefKey, selectors }) => {
        const desiredValue = `${prefs[prefKey] || ""}`.trim();
        selectors.forEach((selector) => {
          const select = document.querySelector(selector);
          if (!(select instanceof HTMLSelectElement)) return;

          sawControl = true;

          if (!desiredValue) {
            select.dataset.rezrateSyncedValue = "";
            return;
          }

          const hasOption = Array.from(select.options || []).some(
            (option) => `${option?.value || ""}`.trim() === desiredValue
          );
          if (!hasOption) {
            allMatched = false;
            return;
          }

          if (`${select.value || ""}`.trim() === desiredValue) {
            select.dataset.rezrateSyncedValue = desiredValue;
            return;
          }

          allMatched = false;
          setNativeSelectValue(select, desiredValue);
          select.dataset.rezrateSyncedValue = desiredValue;
          dispatchNativeChange(select);
        });
      });

      return {
        sawControl,
        isSynced: sawControl && allMatched,
      };
    } catch (e) {
      return {
        sawControl: false,
        isSynced: false,
      };
    }
  };

  const applyAudioVideoOnDefaults = async () => {
    try {
      const prefs = mediaPrefsRef.current || {};
      const client = uitoolkit.client;
      const stream = getMediaStreamIfAvailable();
      if (!client || !stream) return;

      const desiredAudioOn =
        typeof prefs.audioOn === "boolean" ? prefs.audioOn : null;
      const desiredVideoOn =
        typeof prefs.videoOn === "boolean" ? prefs.videoOn : null;

      const user = typeof client.getCurrentUserInfo === "function"
        ? client.getCurrentUserInfo()
        : null;

      const audioCurrentlyOn =
        typeof user?.bAudioOn === "boolean" ? user.bAudioOn : null;
      const videoCurrentlyOn =
        typeof user?.bVideoOn === "boolean" ? user.bVideoOn : null;

      // Let the toolkit preview/session handoff decide the initial ON state.
      // Forcing startAudio/startVideo here can race with the built-in preview flow and
      // surface misleading permission errors even when the browser already granted access.
      if (
        desiredAudioOn === false &&
        audioCurrentlyOn === true &&
        typeof stream.stopAudio === "function"
      ) {
        try {
          await stream.stopAudio();
        } catch (e) {
          // ignore
        }
      }

      if (
        desiredVideoOn === false &&
        videoCurrentlyOn === true &&
        typeof stream.stopVideo === "function"
      ) {
        try {
          await stream.stopVideo();
        } catch (e) {
          // ignore
        }
      }
    } catch (e) {
      // ignore
    }
  };

  const applyBlurBackgroundPreferenceInToolkitUi = () => {
    try {
      const desired = !!mediaPrefsRef.current?.blurBackground;
      const blurButtons = findMenuButtonsByExactText([
        "blur",
        "blur your background",
        "blur my background",
      ]);
      if (blurButtons.length === 0) {
        return {
          sawControl: false,
          isSynced: false,
        };
      }
      let allMatched = true;
      blurButtons.forEach((button) => {
        const isActive = isMirrorMenuButtonActive(button);
        if (isActive !== desired) {
          allMatched = false;
          try {
            button.click();
          } catch (e) {
            // ignore
          }
        }
      });
      return {
        sawControl: true,
        isSynced: allMatched,
      };
    } catch (e) {
      return {
        sawControl: false,
        isSynced: false,
      };
    }
  };

  const applyBlurBackgroundPreferenceIfReady = async () => {
    try {
      if (!virtualBackgroundSupported) {
        return true;
      }
      const stream = getMediaStreamIfAvailable();
      if (!stream || typeof stream.updateVirtualBackgroundImage !== "function") {
        return false;
      }

      const desired = !!mediaPrefsRef.current?.blurBackground;
      await stream.updateVirtualBackgroundImage(desired ? "blur" : undefined);
      return true;
    } catch (e) {
      if (isVirtualBackgroundFailure(e)) {
        try {
          const mediaKey =
            mediaPrefsStorageKeyRef.current ||
            computeMediaPrefsStorageKey(joinConfigRef.current || {});
          mediaPrefsStorageKeyRef.current = mediaKey;
          const nextPrefs = { ...(mediaPrefsRef.current || {}) };
          nextPrefs.blurBackground = false;
          mediaPrefsRef.current = nextPrefs;
          writeMediaPrefs(mediaKey, nextPrefs);
          applyBlurBackgroundPreferenceInToolkitUi();
        } catch (inner) {
          // ignore
        }
        return true;
      }
      return false;
    }
  };

  const applyStoredMediaPrefsIfPossible = async () => {
    const now = Date.now();
    const state = mediaPrefsApplyStateRef.current || {
      lastAttemptAt: 0,
      lastAppliedSnapshot: null,
    };
    // Avoid hammering enumerateDevices/switch calls; the UI sync loop runs frequently.
    if (now - (state.lastAttemptAt || 0) < 4000) {
      return;
    }
    state.lastAttemptAt = now;
    mediaPrefsApplyStateRef.current = state;

    const prefs = mediaPrefsRef.current || {};
    if (prefs.blurBackground && !virtualBackgroundSupported) {
      try {
        const mediaKey =
          mediaPrefsStorageKeyRef.current ||
          computeMediaPrefsStorageKey(joinConfigRef.current || {});
        mediaPrefsStorageKeyRef.current = mediaKey;
        const nextPrefs = { ...(mediaPrefsRef.current || {}) };
        nextPrefs.blurBackground = false;
        mediaPrefsRef.current = nextPrefs;
        writeMediaPrefs(mediaKey, nextPrefs);
      } catch (e) {
        // ignore
      }
    }
    // Ensure we have device label → id maps once permissions are granted.
    await refreshMediaDeviceLabelMaps();

    // Device IDs are optional. If stored device is missing, fall back to system default (do nothing).
    const snapshot = {
      cameraId: `${prefs.cameraId || ""}`.trim(),
      microphoneId: `${prefs.microphoneId || ""}`.trim(),
      speakerId: `${prefs.speakerId || ""}`.trim(),
      blurBackground: !!prefs.blurBackground && virtualBackgroundSupported,
    };

    const alreadyApplied =
      state.lastAppliedSnapshot &&
      state.lastAppliedSnapshot.cameraId === snapshot.cameraId &&
      state.lastAppliedSnapshot.microphoneId === snapshot.microphoneId &&
      state.lastAppliedSnapshot.speakerId === snapshot.speakerId &&
      state.lastAppliedSnapshot.blurBackground === snapshot.blurBackground;

    const cameraId = alreadyApplied
      ? ""
      : await resolveAvailableDeviceId({
          type: "camera",
          preferredId: snapshot.cameraId,
        });
    const micId = alreadyApplied
      ? ""
      : await resolveAvailableDeviceId({
          type: "microphone",
          preferredId: snapshot.microphoneId,
        });
    const speakerId = alreadyApplied
      ? ""
      : await resolveAvailableDeviceId({
          type: "speaker",
          preferredId: snapshot.speakerId,
        });

    // If a preferred device is no longer available, fall back to system default and persist the fallback.
    const missingCamera = !alreadyApplied && snapshot.cameraId && !cameraId;
    const missingMic = !alreadyApplied && snapshot.microphoneId && !micId;
    const missingSpeaker = !alreadyApplied && snapshot.speakerId && !speakerId;
    if (missingCamera || missingMic || missingSpeaker) {
      const nextPrefs = { ...(mediaPrefsRef.current || {}) };
      if (missingCamera) nextPrefs.cameraId = "";
      if (missingMic) nextPrefs.microphoneId = "";
      if (missingSpeaker) nextPrefs.speakerId = "";
      mediaPrefsRef.current = nextPrefs;
      const mediaKey =
        mediaPrefsStorageKeyRef.current ||
        computeMediaPrefsStorageKey(joinConfigRef.current || {});
      mediaPrefsStorageKeyRef.current = mediaKey;
      writeMediaPrefs(mediaKey, nextPrefs);
      snapshot.cameraId = nextPrefs.cameraId;
      snapshot.microphoneId = nextPrefs.microphoneId;
      snapshot.speakerId = nextPrefs.speakerId;
      snapshot.blurBackground = !!nextPrefs.blurBackground;
    }

    let cameraApplied = !cameraId;
    let micApplied = !micId;
    let speakerApplied = !speakerId;
    let blurApplied = alreadyApplied;
    const isInJoinVideoHandoffWindow =
      sessionJoinedAtRef.current > 0 && now - sessionJoinedAtRef.current < 4000;

    try {
      // The toolkit preview can still be transferring the active camera into the live session
      // immediately after join. Defer direct camera switches briefly so we do not interrupt
      // that handoff and trigger a false browser-permission failure toast.
      if (cameraId && !isInJoinVideoHandoffWindow) {
        cameraApplied = await safeSwitchToDevice({
          type: "camera",
          deviceId: cameraId,
        });
      }
      if (micId) {
        micApplied = await safeSwitchToDevice({
          type: "microphone",
          deviceId: micId,
        });
      }
      if (speakerId) {
        speakerApplied = await safeSwitchToDevice({
          type: "speaker",
          deviceId: speakerId,
        });
      }
    } catch (e) {
      // ignore
    }

    if (!alreadyApplied) {
      try {
        blurApplied = await applyBlurBackgroundPreferenceIfReady();
      } catch (e) {
        blurApplied = false;
      }
    }

    if (
      !alreadyApplied &&
      cameraApplied &&
      micApplied &&
      speakerApplied &&
      blurApplied
    ) {
      state.lastAppliedSnapshot = snapshot;
      mediaPrefsApplyStateRef.current = state;
    }

    // Keep the visible toolkit controls in sync with the stored preference.
    try {
      applyBlurBackgroundPreferenceInToolkitUi();
    } catch (e) {
      // ignore
    }

    // Apply desired audio/video on state after switching devices.
    await applyAudioVideoOnDefaults();
  };

  const syncMirrorToggleInToolkitUi = () => {
    try {
      const desired = !!mirrorPrefRef.current;
      let sawMirrorControl = false;
      let allMatched = true;

      const checkboxSelectors = [
        'input#mirror-video[type="checkbox"]',
        'input#uikit-preview-vb-mirror[type="checkbox"]',
      ];

      checkboxSelectors.forEach((selector) => {
        const element = document.querySelector(selector);
        if (!element) return;
        sawMirrorControl = true;
        const isChecked = !!element.checked;
        if (isChecked !== desired) {
          allMatched = false;
          try {
            element.click();
          } catch (e) {
            // ignore
          }
        }
      });

      const mirrorMenuButtons = Array.from(
        document.querySelectorAll("button")
      ).filter((button) => isMirrorMenuButton(button));

      mirrorMenuButtons.forEach((button) => {
        sawMirrorControl = true;
        const isActive = isMirrorMenuButtonActive(button);
        if (isActive !== desired) {
          allMatched = false;
          try {
            button.click();
          } catch (e) {
            // ignore
          }
        }
      });

      if (
        !sawMirrorControl &&
        isSessionActive &&
        !mirrorUiAutoOpenTriedRef.current
      ) {
        const startedAt = mirrorUiSyncStartedAtRef.current || 0;
        const elapsedMs = Date.now() - startedAt;
        if (elapsedMs >= 8000) {
          mirrorUiAutoOpenTriedRef.current = tryOpenVideoOptionsMenu();
        }
      }

      return {
        sawControl: sawMirrorControl,
        isSynced: sawMirrorControl && allMatched,
      };
    } catch (e) {
      return {
        sawControl: false,
        isSynced: false,
      };
    }
  };

  const syncBlurToggleInToolkitUi = () => {
    try {
      return applyBlurBackgroundPreferenceInToolkitUi();
    } catch (e) {
      return {
        sawControl: false,
        isSynced: false,
      };
    }
  };

  const startMirrorUiSync = () => {
    clearMirrorUiSyncTimer();
    clearMirrorUiObserver();
    mirrorUiSyncStartedAtRef.current = Date.now();
    mirrorUiAutoOpenTriedRef.current = false;

    const runSync = () => {
      // Best-effort: if video becomes available later (user turns it on), apply preference again.
      applyMirrorPreferenceIfReady();
      // Best-effort: apply stored device/audio/video/background preferences as the toolkit becomes ready.
      void applyStoredMediaPrefsIfPossible();
      const mirrorSync = syncMirrorToggleInToolkitUi();
      const deviceSync = syncStoredDeviceSelectorsInToolkitUi();
      const blurSync = syncBlurToggleInToolkitUi();
      const elapsedMs = Date.now() - (mirrorUiSyncStartedAtRef.current || Date.now());
      const sawAnyControl =
        mirrorSync.sawControl || deviceSync.sawControl || blurSync.sawControl;
      const everythingSynced =
        (!mirrorSync.sawControl || mirrorSync.isSynced) &&
        (!deviceSync.sawControl || deviceSync.isSynced) &&
        (!blurSync.sawControl || blurSync.isSynced);

      if ((sawAnyControl && everythingSynced) || elapsedMs >= 30000) {
        clearMirrorUiSyncTimer();
        clearMirrorUiObserver();
      }
      return sawAnyControl && everythingSynced;
    };

    if (runSync()) {
      return;
    }

    if (typeof MutationObserver === "function" && document?.body) {
      mirrorUiObserverRef.current = new MutationObserver(() => {
        runSync();
      });
      mirrorUiObserverRef.current.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class", "aria-checked", "checked", "value"],
      });
    }

    mirrorUiSyncIntervalRef.current = window.setInterval(() => {
      runSync();
    }, 1000);
  };

  const applyMirrorPreferenceIfReady = () => {
    try {
      const desired = !!mirrorPrefRef.current;
      let applied = false;

      // Apply regardless of whether self video is currently on.
      // Users can join, wait, then start camera later; we still want the default/preference
      // to take effect when video starts.

      if (typeof uitoolkit?.mirrorVideo === "function") {
        try {
          uitoolkit.mirrorVideo(desired);
          applied = true;
        } catch (e) {
          // ignore and fall back below
        }
      }

      // UI toolkit exposes mirror via the SDK client (preferred) in newer builds.
      const client = uitoolkit.client;
      if (client && typeof client.mirrorVideo === "function") {
        client.mirrorVideo(desired);
        applied = true;
      }

      // Fallback: some SDK builds expose this on the media stream.
      const mediaStream = client?.getMediaStream?.();
      if (mediaStream && typeof mediaStream.mirrorVideo === "function") {
        mediaStream.mirrorVideo(desired);
        applied = true;
      }

      // Some SDK builds only apply the mirror state once self video is actually running.
      // Keep retrying until either:
      // - we can apply the mirror via DOM transform on the rendered self-view, or
      // - self video is confirmed on and we successfully invoked the SDK mirror API.
      const renderedApplied = applyMirrorPreferenceToRenderedVideo();
      const user = typeof client?.getCurrentUserInfo === "function"
        ? client.getCurrentUserInfo()
        : null;
      const isVideoOn = user?.bVideoOn === true;
      return renderedApplied || (applied && isVideoOn);
    } catch (e) {
      return applyMirrorPreferenceToRenderedVideo();
    }
  };

  const installMirrorPreferenceUiListener = () => {
    if (mirrorUiClickHandlerRef.current) {
      return;
    }

    const handler = (event) => {
      try {
        // Ignore synthetic clicks from our own auto-sync.
        if (event && event.isTrusted === false) {
          return;
        }

        const target = event?.target;
        if (!target) return;

        const selectTarget =
          target instanceof HTMLSelectElement
            ? target
            : target.closest?.("select") || null;
        const mirrorCheckbox =
          target.closest?.('input#mirror-video[type="checkbox"]') ||
          target.closest?.('input#uikit-preview-vb-mirror[type="checkbox"]') ||
          null;
        const mirrorButton = target.closest?.("button") || null;

        const isMirrorButton = mirrorButton && isMirrorMenuButton(mirrorButton);
        const isBlurButton = mirrorButton && isBlurMenuButton(mirrorButton);
        const isPreviewBlurButton =
          mirrorButton && isButtonTextMatch(mirrorButton, ["blur"]);
        const isPreviewNoneButton =
          mirrorButton && isButtonTextMatch(mirrorButton, ["none"]);

        // For device selection, the toolkit uses buttons; we infer selection by matching to known labels.
        const buttonText = normalizeControlText(mirrorButton?.textContent);
        const knownDevices = mediaDevicesByLabelRef.current || {};
        const cameraIdFromLabel =
          buttonText && knownDevices.cameras?.get(buttonText)
            ? knownDevices.cameras.get(buttonText)
            : "";
        const micIdFromLabel =
          buttonText && knownDevices.microphones?.get(buttonText)
            ? knownDevices.microphones.get(buttonText)
            : "";
        const speakerIdFromLabel =
          buttonText && knownDevices.speakers?.get(buttonText)
            ? knownDevices.speakers.get(buttonText)
            : "";

        const isDeviceSelectionButton = !!(
          cameraIdFromLabel ||
          micIdFromLabel ||
          speakerIdFromLabel
        );
        const selectId = `${selectTarget?.id || ""}`.trim();
        const isDeviceSelect = [
          "uikit-preview-camera-select",
          "cameraSelect",
          "video-input-select",
          "uikit-preview-mic-select",
          "settings-microphone",
          "audio-input-select",
          "uikit-preview-speaker-select",
          "speaker",
          "audio-output-select",
        ].includes(selectId);

        // We only care about:
        // - mirror toggle (checkbox/menu)
        // - blur background menu item
        // - preview blur option
        // - device selection items
        if (
          !mirrorCheckbox &&
          !isMirrorButton &&
          !isBlurButton &&
          !isPreviewBlurButton &&
          !isPreviewNoneButton &&
          !isDeviceSelectionButton &&
          !isDeviceSelect
        ) {
          return;
        }

        window.setTimeout(() => {
          try {
            // Always refresh label maps after user interacts with device controls.
            void refreshMediaDeviceLabelMaps();

            // Mirror preference
            if (mirrorCheckbox || isMirrorButton) {
              let desired = !!mirrorPrefRef.current;
              if (mirrorCheckbox) {
                desired = !!mirrorCheckbox.checked;
              } else if (mirrorButton) {
                desired = isMirrorMenuButtonActive(mirrorButton);
              }

              mirrorPrefRef.current = desired;
              const storageKey =
                mirrorStorageKeyRef.current ||
                computeMirrorStorageKey(joinConfigRef.current || {});
              mirrorStorageKeyRef.current = storageKey;
              writeMirrorPreference(storageKey, desired);
              applyMirrorPreferenceIfReady();
            }

            // Blur background preference
            if (isBlurButton && mirrorButton) {
              const desiredBlur = isMirrorMenuButtonActive(mirrorButton);
              mediaPrefsRef.current = {
                ...mediaPrefsRef.current,
                blurBackground: desiredBlur,
              };
            }
            if (isPreviewBlurButton) {
              mediaPrefsRef.current = {
                ...mediaPrefsRef.current,
                blurBackground: true,
              };
            }
            if (isPreviewNoneButton) {
              mediaPrefsRef.current = {
                ...mediaPrefsRef.current,
                blurBackground: false,
              };
            }

            // Device selection (best-effort by label → deviceId)
            if (isDeviceSelectionButton) {
              const nextPrefs = { ...mediaPrefsRef.current };
              if (cameraIdFromLabel) nextPrefs.cameraId = cameraIdFromLabel;
              if (micIdFromLabel) nextPrefs.microphoneId = micIdFromLabel;
              if (speakerIdFromLabel) nextPrefs.speakerId = speakerIdFromLabel;
              mediaPrefsRef.current = nextPrefs;
              void applyStoredMediaPrefsIfPossible();
            }
            if (isDeviceSelect && selectTarget) {
              const nextPrefs = { ...mediaPrefsRef.current };
              const selectedValue = `${selectTarget.value || ""}`.trim();
              if (
                [
                  "uikit-preview-camera-select",
                  "cameraSelect",
                  "video-input-select",
                ].includes(selectId)
              ) {
                nextPrefs.cameraId = selectedValue;
              }
              if (
                [
                  "uikit-preview-mic-select",
                  "settings-microphone",
                  "audio-input-select",
                ].includes(selectId)
              ) {
                nextPrefs.microphoneId = selectedValue;
              }
              if (
                [
                  "uikit-preview-speaker-select",
                  "speaker",
                  "audio-output-select",
                ].includes(selectId)
              ) {
                nextPrefs.speakerId = selectedValue;
              }
              mediaPrefsRef.current = nextPrefs;
              void applyStoredMediaPrefsIfPossible();
            }

            // Persist updated media prefs if we have a storage key.
            const mediaKey =
              mediaPrefsStorageKeyRef.current ||
              computeMediaPrefsStorageKey(joinConfigRef.current || {});
            mediaPrefsStorageKeyRef.current = mediaKey;
            writeMediaPrefs(mediaKey, mediaPrefsRef.current);
          } catch (e) {
            // ignore
          }
        }, 0);
      } catch (e) {
        // ignore
      }
    };

    mirrorUiClickHandlerRef.current = handler;
    document.addEventListener("click", handler, true);
    document.addEventListener("change", handler, true);
  };

  const ensureMirrorAppliesWhenVideoStarts = () => {
    clearMirrorUserUpdatedListener();
    try {
      const client = uitoolkit.client;
      if (!client || typeof client.on !== "function") {
        return;
      }
      const onUserUpdated = () => {
        const applied = applyMirrorPreferenceIfReady();
        // Persist audio/video on/off preference when the SDK updates user state.
        try {
          const current = typeof client.getCurrentUserInfo === "function"
            ? client.getCurrentUserInfo()
            : null;
          if (current) {
            const nextPrefs = { ...mediaPrefsRef.current };
            if (typeof current.bAudioOn === "boolean") {
              nextPrefs.audioOn = current.bAudioOn;
            }
            if (typeof current.bVideoOn === "boolean") {
              nextPrefs.videoOn = current.bVideoOn;
            }
            mediaPrefsRef.current = nextPrefs;
            const mediaKey =
              mediaPrefsStorageKeyRef.current ||
              computeMediaPrefsStorageKey(joinConfigRef.current || {});
            mediaPrefsStorageKeyRef.current = mediaKey;
            writeMediaPrefs(mediaKey, nextPrefs);
          }
        } catch (e) {
          // ignore
        }
        if (applied) {
          clearMirrorUserUpdatedListener();
        }
      };
      mirrorUserUpdatedHandlerRef.current = onUserUpdated;
      client.on("user-updated", onUserUpdated);
    } catch (e) {
      mirrorUserUpdatedHandlerRef.current = null;
    }
  };

  const formatEndsIn = (totalSeconds) => {
    const seconds = Math.max(0, parseInt(totalSeconds, 10) || 0);

    if (seconds >= 3600) {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${minutes}m`;
    }

    if (seconds >= 120) {
      const minutes = Math.ceil(seconds / 60);
      return `${minutes} minute${minutes === 1 ? "" : "s"}`;
    }

    const minutes = Math.floor(seconds / 60);
    const remainder = seconds % 60;
    return `${minutes}:${`${remainder}`.padStart(2, "0")}`;
  };

  const initMirrorPreferenceForConfig = (config) => {
    const storageKey = computeMirrorStorageKey(config);
    mirrorStorageKeyRef.current = storageKey;
    // Product requirement: mirror should default to ON, but honor user preference
    // if they explicitly turned it off/on via the toolkit UI.
    const stored = readMirrorPreference(storageKey);
    const initial = stored === null ? true : stored;
    mirrorPrefRef.current = initial;
    if (stored === null) {
      writeMirrorPreference(storageKey, initial);
    }
  };

  const initMediaPrefsForConfig = (config) => {
    const storageKey = computeMediaPrefsStorageKey(config);
    mediaPrefsStorageKeyRef.current = storageKey;

    const stored = readMediaPrefs(storageKey);
    const normalized = {
      cameraId: "",
      microphoneId: "",
      speakerId: "",
      blurBackground: false,
      audioOn: null,
      videoOn: null,
      ...(stored && typeof stored === "object" ? stored : {}),
    };

    if (typeof normalized.audioOn !== "boolean") normalized.audioOn = null;
    if (typeof normalized.videoOn !== "boolean") normalized.videoOn = null;

    mediaPrefsRef.current = normalized;
    if (!stored) {
      writeMediaPrefs(storageKey, normalized);
    }
  };

  const storeRefreshToken = (rt) => {
    try {
      if (rt) {
        window.sessionStorage.setItem("rezrate_rt", rt);
      }
    } catch (e) {
      // ignore
    }
  };

  const readStoredRefreshToken = () => {
    try {
      return window.sessionStorage.getItem("rezrate_rt");
    } catch (e) {
      return null;
    }
  };

  const decodeJwtPayload = (jwt) => {
    try {
      const parts = `${jwt || ""}`.split(".");
      if (parts.length < 2) return null;
      const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
      const json = decodeURIComponent(
        atob(b64)
          .split("")
          .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
          .join("")
      );
      return JSON.parse(json);
    } catch {
      return null;
    }
  };

  const resolveUserNameFromJwtPayload = (payload) => {
    const candidates = [
      payload?.user_name,
      payload?.userName,
      payload?.name,
      payload?.display_name,
      payload?.displayName,
    ];

    for (const value of candidates) {
      const normalized = `${value || ""}`.trim();
      if (normalized) return normalized;
    }

    return "";
  };

  const setServerOffsetFromServerNowSeconds = (serverNowSeconds) => {
    const serverNow = Number(serverNowSeconds);
    if (!Number.isFinite(serverNow) || serverNow <= 0) return;
    serverOffsetMsRef.current = serverNow * 1000 - Date.now();
  };

  const getServerNowMs = () => Date.now() + (serverOffsetMsRef.current || 0);

  const isJwtExpired = (jwt) => {
    const payload = decodeJwtPayload(jwt);
    if (!payload?.exp) return false;
    const now = Math.floor(Date.now() / 1000);
    return now >= payload.exp - 5;
  };

  const getJoinParams = () => {
    const queryParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(
      (window.location.hash || "").replace(/^#/, "")
    );

    const sessionNameFromQuery = queryParams.get("sessionName");
    const userIdFromQuery = queryParams.get("userId");

    // Back-compat: ?session=<sessionName>-<userId>
    const legacySession = queryParams.get("session");
    let sessionNameFromLegacy = null;
    let userIdFromLegacy = null;
    if (legacySession) {
      const splitIndex = legacySession.indexOf("-");
      if (splitIndex > 0) {
        sessionNameFromLegacy = legacySession.slice(0, splitIndex);
        userIdFromLegacy = legacySession.slice(splitIndex + 1);
      }
    }

    const jwtFromUrl =
      hashParams.get("t") ||
      hashParams.get("jwt") ||
      hashParams.get("videoSDKJWT") ||
      hashParams.get("token") ||
      queryParams.get("t") ||
      queryParams.get("jwt") ||
      queryParams.get("videoSDKJWT") ||
      queryParams.get("token");

    const rtFromUrl =
      hashParams.get("m") || hashParams.get("rt") || queryParams.get("rt");

    const guestTokenFromUrl =
      hashParams.get("j") ||
      hashParams.get("guest") ||
      queryParams.get("guest");

    const sessionName =
      sessionNameFromQuery || sessionNameFromLegacy || queryParams.get("tpc");
    const userId = userIdFromQuery || userIdFromLegacy;

    const hasExplicitJoinParams = !!(
      sessionName ||
      userId ||
      jwtFromUrl ||
      rtFromUrl ||
      guestTokenFromUrl
    );

    return {
      sessionName:
        sessionName,
      userId,
      jwt: jwtFromUrl,
      userName: hashParams.get("userName") || queryParams.get("userName"),
      topic:
        hashParams.get("topic") ||
        hashParams.get("Topic") ||
        queryParams.get("topic") ||
        queryParams.get("Topic"),
      role:
        hashParams.get("role") ||
        queryParams.get("role") ||
        queryParams.get("role_type"),
      refreshUrl: queryParams.get("refreshUrl"),
      returnUrl: queryParams.get("returnUrl"),
      rt: rtFromUrl || readStoredRefreshToken(),
      rtFromUrl,
      guestToken: guestTokenFromUrl,
      hasExplicitJoinParams,
    };
  };

  const getExplicitJoinIntentKey = (params = {}) => {
    const normalizedSessionName = `${params?.sessionName || ""}`.trim();
    const normalizedUserId = `${params?.userId || ""}`.trim();
    const normalizedJwt = `${params?.jwt || ""}`.trim();
    const normalizedRt = `${params?.rtFromUrl || ""}`.trim();
    const normalizedGuest = `${params?.guestToken || ""}`.trim();
    const currentPath = `${window.location.pathname || ""}`;
    const currentSearch = `${window.location.search || ""}`;
    const currentHash = `${window.location.hash || ""}`;

    if (
      !normalizedSessionName &&
      !normalizedUserId &&
      !normalizedJwt &&
      !normalizedRt &&
      !normalizedGuest
    ) {
      return "";
    }

    return [
      normalizedSessionName,
      normalizedUserId,
      normalizedJwt,
      normalizedRt,
      normalizedGuest,
      currentPath,
      currentSearch,
      currentHash,
    ].join("|");
  };

  const fetchJoinConfig = async ({ refreshUrl, sessionName, rt }) => {
    // Preferred: use refresh token to exchange for a new Video SDK JWT
    if (rt) {
      const apiBase = getApiBase();
      if (!apiBase) return null;

      const response = await fetch(`${apiBase}/candidate/embedded/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: rt }),
      });

      const data = await response.json();
      if (data?.success && data?.config?.videoSDKJWT) {
        return data.config;
      }
      // If this token isn't a candidate refresh token, it may be a guest join token.
      const guestConfig = await fetchGuestJoinConfig({ guestToken: rt });
      if (guestConfig?.videoSDKJWT) {
        return guestConfig;
      }
      return null;
    }

    if (!refreshUrl) return null;

    const response = await fetch(refreshUrl, { method: "GET" });
    const data = await response.json();

    // Candidate endpoint returns: { success, config, enableEmbeddedMeetings }
    if (data?.success && data?.config?.videoSDKJWT) {
      return data.config;
    }

    // Some endpoints wrap config under { success, config: { config: ... } }
    if (data?.success && data?.config?.config?.videoSDKJWT) {
      return data.config.config;
    }

    // Fallback: if refresh endpoint returns just signature
    if (data?.signature) {
      return {
        videoSDKJWT: data.signature,
        signature: data.signature,
        sessionName,
        userName: "RezRATE User",
        role: 0,
      };
    }

    return null;
  };

  const fetchGuestJoinConfig = async ({ guestToken }) => {
    if (!guestToken) return null;
    const apiBase = getApiBase();
    if (!apiBase) return null;

    const response = await fetch(`${apiBase}/candidate/embedded/guest/join`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ guestToken }),
    });

    const data = await response.json();
    if (data?.success && data?.config?.videoSDKJWT) {
      return data.config;
    }
    return null;
  };

  const fetchEmbeddedStatus = async () => {
    const apiBase = getApiBase();
    if (!apiBase) return null;

    const token =
      joinConfigRef.current?.videoSDKJWT ||
      joinConfigRef.current?.signature ||
      null;
    if (!token) return null;

    const response = await fetch(`${apiBase}/candidate/embedded/status`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ videoSDKJWT: token }),
    });
    const data = await response.json();
    if (!data?.success) return null;
    return data;
  };

  const getEndAtFromConfig = (cfg) => {
    const token = cfg?.videoSDKJWT || cfg?.signature || null;
    const payload = token ? decodeJwtPayload(token) : null;
    return cfg?.endAt || payload?.rezrate_end_at || payload?.rezrateEndAt || null;
  };

  const clearEndTimers = () => {
    if (endTimerRef.current) {
      clearTimeout(endTimerRef.current);
      endTimerRef.current = null;
    }
    if (endCountdownIntervalRef.current) {
      clearInterval(endCountdownIntervalRef.current);
      endCountdownIntervalRef.current = null;
    }
    setEndsInSeconds(null);
  };

  const scheduleEndAt = (endAtSeconds) => {
    const endAt = Number(endAtSeconds);
    if (!Number.isFinite(endAt) || endAt <= 0) return;

    const now = Math.floor(getServerNowMs() / 1000);
    if (endAt <= now + 5) return;

    clearEndTimers();

    endCountdownIntervalRef.current = setInterval(() => {
      const current = Math.floor(getServerNowMs() / 1000);
      const remaining = endAt - current;
      if (remaining <= 0) {
        setEndsInSeconds(0);
        if (endCountdownIntervalRef.current) {
          clearInterval(endCountdownIntervalRef.current);
          endCountdownIntervalRef.current = null;
        }
      } else {
        setEndsInSeconds(remaining);
      }
    }, 1000);

    endTimerRef.current = setTimeout(() => {
      try {
        const shouldEndForAll = (joinConfigRef.current?.role || 0) === 1;
        if (
          shouldEndForAll &&
          uitoolkit.client &&
          typeof uitoolkit.client.leave === "function"
        ) {
          void leaveSessionForAllSafe({ endForAll: true });
        } else if (sessionContainerRef.current) {
          uitoolkit.closeSession(sessionContainerRef.current);
        }
        handleSessionEnded();
      } catch (e) {
        fallbackSessionTermination();
      }
    }, (endAt - now) * 1000);
  };

  const joinFromQueryParams = async () => {
    setError(null);
    setHandoffNotice("");
    setIsSessionActive(false);
    sessionContainerRef.current = document.getElementById("sessionContainer");

    if (!sessionContainerRef.current) {
      setError("Session container not found");
      return;
    }

    let {
      sessionName,
      userId,
      jwt,
      userName,
      role,
      topic,
      refreshUrl,
      returnUrl,
      rt,
      rtFromUrl,
      guestToken,
      hasExplicitJoinParams,
    } = getJoinParams();

    if (hasExplicitJoinParams) {
      const joinIntentKey = getExplicitJoinIntentKey({
        sessionName,
        userId,
        jwt,
        rtFromUrl,
        guestToken,
      });
      if (joinIntentKey) {
        lastHandledJoinIntentKeyRef.current = joinIntentKey;
      }
    }

    if (rt) {
      storeRefreshToken(rt);
    }

    const jwtPayload = jwt ? decodeJwtPayload(jwt) : null;
    if (!sessionName && jwtPayload?.tpc) {
      sessionName = jwtPayload.tpc;
    }

    try {
      // Guest join link (no attendance tracking). Highest priority.
      if (guestToken) {
        const guestConfig = await fetchGuestJoinConfig({ guestToken });
        if (guestConfig?.videoSDKJWT) {
          joinSession({
            ...guestConfig,
            signature: guestConfig.videoSDKJWT,
          });
          return;
        }
        setError("Unable to join meeting with provided link.");
        showJoinFlow();
        return;
      }

      // Preferred: portal supplies a ready-to-use Video SDK JWT in the URL fragment (not sent to the server).
      if (jwt && !isJwtExpired(jwt)) {
        // If we have a refresh URL/token, prefer fetching the server-side join config even when the
        // JWT is still valid so we can pick up `topic`, `endAt`, and server clock skew corrections.
        // (Otherwise the UI shows the opaque internal session name as the meeting title.)
        if (rt || refreshUrl) {
          try {
            const enrichedConfig = await fetchJoinConfig({
              refreshUrl,
              sessionName,
              rt,
            });
            if (enrichedConfig?.videoSDKJWT) {
              joinSession({
                ...enrichedConfig,
                signature: enrichedConfig.videoSDKJWT,
              });
              return;
            }
          } catch (e) {
            // ignore and fall back to using the provided JWT
          }
        }

        // Use JWT iat as a server-time estimate to correct client clock skew.
        if (Number.isFinite(Number(jwtPayload?.iat))) {
          setServerOffsetFromServerNowSeconds(Number(jwtPayload.iat));
        }
        stripUrlFragment();
        joinSession({
          videoSDKJWT: jwt,
          signature: jwt,
          sessionName,
          userName:
            userName ||
            resolveUserNameFromJwtPayload(jwtPayload) ||
            "RezRATE User",
          userIdentity:
            jwtPayload?.user_identity || jwtPayload?.userIdentity || undefined,
          topic: topic || undefined,
          role: Number.isFinite(parseInt(jwtPayload?.role_type, 10))
            ? parseInt(jwtPayload.role_type, 10)
            : Number.isFinite(parseInt(role, 10))
              ? parseInt(role, 10)
              : 0,
          featuresOptions: DEFAULT_FEATURES_OPTIONS,
        });
        return;
      }

      // If JWT is missing/expired and we have a refresh token or refresh URL (candidate flows), fetch a new token.
      if (rt || refreshUrl) {
        const refreshedConfig = await fetchJoinConfig({
          refreshUrl,
          sessionName,
          rt,
        });
        if (refreshedConfig?.videoSDKJWT) {
          joinSession({
            ...refreshedConfig,
            signature: refreshedConfig.videoSDKJWT,
          });
          return;
        }
      }

      if (!sessionName) {
        setError("Missing sessionName in URL");
        showJoinFlow();
        return;
      }

      if (!userId) {
        setError("Missing userId in URL (required when jwt is not provided)");
        showJoinFlow();
        return;
      }

      const response = await fetch(`${authEndpoint}/session/join`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sessionName,
          userId,
        }),
      });

      const data = await response.json();

      if (data.success && data.config) {
        joinSession({
          ...data.config,
          featuresOptions: DEFAULT_FEATURES_OPTIONS,
        });
      } else {
        setError(data.message || "Failed to join session");
        showJoinFlow();
      }
    } catch (error) {
      const msg = error.message || "An error occurred while joining";
      if (returnUrl) {
        setError(`${msg}\n\nReturn to schedule: ${returnUrl}`);
      } else {
        setError(msg);
      }
      showJoinFlow();
    } finally {
    }
  };

  const sessionJoined = () => {
    console.log("Session joined successfully");
    setIsSessionActive(true);
    claimPrimaryVideoWindow("session-joined", { force: true });
    sessionJoinedAtRef.current = Date.now();
    ensureToolkitAudioErrorPatch();
    installMirrorPreferenceUiListener();
    hideJoinFlow();
    // Do not start inactivity timer on join; only start when the user backgrounds the tab/window.
    // "Mirror my video" default is ON, but we respect per-user preference when present.
    // The UI toolkit sometimes initializes media lazily, so retry briefly until the media stream exists.
    clearMirrorInitTimer();
    clearMirrorUiSyncTimer();
    clearMirrorUiObserver();
    clearMirrorUserUpdatedListener();
    ensureMirrorAppliesWhenVideoStarts();
    startMirrorUiSync();
    try {
      const maxAttempts = 120; // ~30s at 250ms
      let attempts = 0;
      mirrorInitIntervalRef.current = window.setInterval(() => {
        attempts += 1;
        const applied = applyMirrorPreferenceIfReady();
        if (applied || attempts >= maxAttempts) {
          clearMirrorInitTimer();
          if (applied) {
            clearMirrorUserUpdatedListener();
          }
        }
      }, 250);
    } catch (e) {}

    // Schedule end-of-passing termination when provided by the API/JWT.
    try {
      const endAt = getEndAtFromConfig(joinConfigRef.current || {});
      scheduleEndAt(endAt);
    } catch (e) {}
  };

  const sessionClosed = () => {
    console.log("Session closed");
    setError(null);
    setIsSessionActive(false);
    sessionJoinedAtRef.current = 0;
    clearAudioErrorPatchTimer();
    clearInactivityTimer();
    clearMirrorInitTimer();
    clearMirrorUiSyncTimer();
    clearMirrorUiObserver();
    clearMirrorUserUpdatedListener();
    clearMirrorUiClickListener();
    clearEndTimers();
    setDisplayTopic("");
    showJoinFlow();
  };

  const sessionDestroyed = () => {
    console.log("Session destroyed");
    setError(null);
    setIsSessionActive(false);
    sessionJoinedAtRef.current = 0;
    clearAudioErrorPatchTimer();
    clearInactivityTimer();
    clearMirrorInitTimer();
    clearMirrorUiSyncTimer();
    clearMirrorUiObserver();
    clearMirrorUserUpdatedListener();
    clearMirrorUiClickListener();
    clearEndTimers();
    setDisplayTopic("");
  };

  const joinSession = (config) => {
    try {
      const featuresOptions = {
        ...DEFAULT_FEATURES_OPTIONS,
        ...(config?.featuresOptions || {}),
        header: {
          ...(DEFAULT_FEATURES_OPTIONS.header || {}),
          ...(config?.featuresOptions?.header || {}),
          enable: false,
        },
      };
      const normalizedConfig = {
        ...config,
        role: Number.isFinite(parseInt(config?.role, 10))
          ? parseInt(config.role, 10)
          : 0,
        topic: resolveDisplayTopic(config),
        featuresOptions,
      };
      // Some server-side join configs still include deprecated UI toolkit keys (e.g. `features`,
      // `options`, `virtualBackground`). Strip them to avoid warnings and reduce variability.
      const sanitizedConfig = { ...normalizedConfig };
      delete sanitizedConfig.features;
      delete sanitizedConfig.options;
      delete sanitizedConfig.virtualBackground;
      try {
        if (!virtualBackgroundSupported) {
          sanitizedConfig.featuresOptions = {
            ...(sanitizedConfig.featuresOptions || {}),
            video: {
              ...(sanitizedConfig.featuresOptions?.video || {}),
              virtualBackground: false,
            },
          };
        }
      } catch (e) {
        // ignore
      }
      // Set host status based on role from API
      setIsHost(sanitizedConfig.role === 1);
      setDisplayTopic(resolveReadableMeetingTitle(sanitizedConfig));
      console.log(
        "User role:",
        sanitizedConfig.role,
        "isHost:",
        sanitizedConfig.role === 1
      );

      if (sessionContainerRef.current) {
        initMirrorPreferenceForConfig(sanitizedConfig);
        initMediaPrefsForConfig(sanitizedConfig);
        joinConfigRef.current = sanitizedConfig;
        if (Number.isFinite(Number(sanitizedConfig?.serverNow))) {
          setServerOffsetFromServerNowSeconds(Number(sanitizedConfig.serverNow));
        }
        installMirrorPreferenceUiListener();
        uitoolkit.joinSession(sessionContainerRef.current, sanitizedConfig);
        ensureToolkitAudioErrorPatch();
        startMirrorUiSync();
        uitoolkit.onSessionJoined(sessionJoined);
        uitoolkit.onSessionClosed(sessionClosed);
        uitoolkit.onSessionDestroyed(sessionDestroyed);
      }
    } catch (error) {
      const msg = formatErrorMessage(error, "Failed to initialize embedded meeting");
      console.error("joinSession failed:", msg);
      setError(msg);
      showJoinFlow();
    }
  };

  const closeSessionDueToInactivity = () => {
    if (isSessionActive) {
      try {
        // Inactivity should not end the session for everyone (even if host).
        if (sessionContainerRef.current) {
          uitoolkit.closeSession(sessionContainerRef.current);
        }
      } catch (e) {
        console.error(
          "Error in closeSessionDueToInactivity:",
          formatErrorMessage(e)
        );
        fallbackSessionTermination();
      }
    }
  };

  // Function for host to end session for all participants
  const endSessionForAll = () => {
    console.log("Host is ending session for all participants");
    if (isSessionActive && isHost) {
      try {
        // Force end the session for all participants (true parameter) when the UI toolkit client is available.
        if (uitoolkit.client && typeof uitoolkit.client.leave === "function") {
          void leaveSessionForAllSafe({ endForAll: true });
        } else if (sessionContainerRef.current) {
          uitoolkit.closeSession(sessionContainerRef.current);
        }
      } catch (e) {
        console.error(
          "Error in endSessionForAll:",
          formatErrorMessage(e)
        );
        fallbackSessionTermination();
      }
    }
  };

  const handleSessionEnded = () => {
    setError(null);
    setIsSessionActive(false);
    clearAudioErrorPatchTimer();
    clearInactivityTimer();
    setShowSessionEndedPopup(true);
    setTimeout(() => {
      setShowSessionEndedPopup(false);
    }, 5000);
    showJoinFlow();
  };

  const fallbackSessionTermination = () => {
    clearAudioErrorPatchTimer();
    if (sessionContainerRef.current) {
      try {
        uitoolkit.closeSession(sessionContainerRef.current);
      } catch (e) {
        console.log(
          "Error calling uitoolkit.closeSession:",
          formatErrorMessage(e)
        );
      }
    }

    try {
      if (uitoolkit.client && typeof uitoolkit.client.leave === "function") {
        // Leave locally; never attempt to end the session for everyone from an error fallback.
        void leaveSessionForAllSafe({ endForAll: false, logUnexpected: false });
      }
    } catch (e) {
      console.log(
        "Error calling uitoolkit.client.leave:",
        formatErrorMessage(e)
      );
    }

    try {
      if (typeof uitoolkit.destroy === "function") {
        uitoolkit.destroy();
      }
    } catch (e) {
      console.log(
        "Error calling uitoolkit.destroy:",
        formatErrorMessage(e)
      );
    }

    handleSessionEnded();
  };
  const startInactivityTimer = () => {
    if (!INACTIVITY_TIMEOUT) return;
    console.log("DIRECT: Starting inactivity timer");
    console.log("DIRECT: Current time:", new Date().toISOString());

    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }

    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }

    setTimeRemaining(Math.floor(INACTIVITY_TIMEOUT / 1000));

    countdownTimerRef.current = setInterval(() => {
      setTimeRemaining((prevTime) => {
        console.log("DIRECT: Countdown tick:", prevTime - 1);
        if (prevTime <= 1) {
          console.log("DIRECT: Countdown reached zero");
          clearInterval(countdownTimerRef.current);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    inactivityTimerRef.current = setTimeout(() => {
      console.log("DIRECT: Inactivity timeout reached");
      closeSessionDueToInactivity();
    }, INACTIVITY_TIMEOUT);
  };

  const clearInactivityTimer = () => {
    console.log("DIRECT: Clearing inactivity timer");

    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }

    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }

    if (INACTIVITY_TIMEOUT) setTimeRemaining(0);
  };

  useEffect(() => {
    return () => {
      if (isSessionActive && sessionContainerRef.current) {
        try {
          if (typeof uitoolkit.offSessionJoined === "function") {
            uitoolkit.offSessionJoined(sessionJoined);
          }
          if (typeof uitoolkit.offSessionClosed === "function") {
            uitoolkit.offSessionClosed(sessionClosed);
          }
          if (typeof uitoolkit.offSessionDestroyed === "function") {
            uitoolkit.offSessionDestroyed(sessionDestroyed);
          }
        } catch (error) {
          console.log(
            "Error removing event listeners:",
            formatErrorMessage(error)
          );
        }
      }
      clearInactivityTimer();
      clearMirrorInitTimer();
      clearMirrorUiSyncTimer();
      clearMirrorUiObserver();
      clearMirrorUserUpdatedListener();
      clearMirrorUiClickListener();
    };
  }, [isSessionActive]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (isSessionActive) {
        if (document.hidden) {
          startInactivityTimer();
        } else {
          clearInactivityTimer();
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    const handleFocus = () => {
      console.log("DIRECT: Window focus event");
      if (isSessionActive) {
        clearInactivityTimer();
      }
    };

    window.addEventListener("focus", handleFocus);

    if (document.hidden && isSessionActive) {
      startInactivityTimer();
    }

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [isSessionActive]);

  useEffect(() => {
    const activityEvents = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
    ];

    const handleActivity = () => {
      if (!document.hidden && isSessionActive) {
        clearInactivityTimer();
      }
    };

    activityEvents.forEach((event) => {
      window.addEventListener(event, handleActivity);
    });

    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [isSessionActive]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      const suppressPrompt = consumeSuppressBeforeUnloadPrompt();
      if (isSessionActive) {
        console.log("Tab closing - forcefully ending Zoom session");

        // Leave locally on tab close; do not end the session for everyone.
        if (sessionContainerRef.current) {
          try {
            uitoolkit.closeSession(sessionContainerRef.current);
            console.log("UI toolkit closeSession called on tab close");
          } catch (e) {
            console.log("Error with UI toolkit closeSession on tab close:", e);
          }
        }

        // Try additional fallback methods
        try {
          if (
            uitoolkit.client &&
            typeof uitoolkit.client.leave === "function"
          ) {
            // Leave locally; do not end the session for everyone (candidates are not hosts).
            void leaveSessionForAllSafe({
              endForAll: false,
              logUnexpected: false,
            });
          }
        } catch (e) {}

        try {
          if (typeof uitoolkit.destroy === "function") {
            uitoolkit.destroy();
          }
        } catch (e) {}

        if (!suppressPrompt) {
          // Standard beforeunload dialog
          event.preventDefault();
          event.returnValue = "";
        } else {
          logVideoWindowEvent("handoff.unload.prompt.suppressed", {
            reason: "handoff-navigation",
            isSessionActive: true,
          });
        }
      }
    };

    // Add both beforeunload and unload handlers for better coverage
    window.addEventListener("beforeunload", handleBeforeUnload);

    // Add a separate unload handler as a last resort
    const handleUnload = () => {
      if (isSessionActive) {
        console.log("Page unloading - final attempt to end session");

        // Last attempt to leave locally
        if (sessionContainerRef.current) {
          try {
            uitoolkit.closeSession(sessionContainerRef.current);
          } catch (e) {}
        }

        if (navigator.sendBeacon) {
          try {
          } catch (e) {}
        }
      }
    };

    window.addEventListener("unload", handleUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("unload", handleUnload);
    };
  }, [isSessionActive]);

  useEffect(() => {
    isSessionActiveRef.current = isSessionActive;
  }, [isSessionActive]);

  useEffect(() => {
    // Keep a stable window name so portal named-target joins can reuse this tab when needed.
    if (!isTopLevelVideoWindowContext()) {
      return undefined;
    }

    ensureNamedVideoWindow("react-mount");

    const handlePageShow = () => {
      ensureNamedVideoWindow("pageshow");
      claimPrimaryVideoWindow("pageshow");
    };
    const handleFocusForName = () => {
      ensureNamedVideoWindow("focus");
      claimPrimaryVideoWindow("focus");
    };

    window.addEventListener("pageshow", handlePageShow);
    window.addEventListener("focus", handleFocusForName);

    return () => {
      window.removeEventListener("pageshow", handlePageShow);
      window.removeEventListener("focus", handleFocusForName);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const maybeReuseExistingWindow = async () => {
      const {
        sessionName,
        userId,
        jwt,
        rt,
        rtFromUrl,
        guestToken,
        hasExplicitJoinParams,
      } = getJoinParams();

      // Auto-join when the URL includes any join-relevant params.
      // - sessionName: new format (preferred)
      // - jwt: fragment-delivered Video SDK JWT
      // - userId: legacy join format uses API join
      if (!(sessionName || jwt || userId || rt || guestToken)) {
        return;
      }

      if (hasExplicitJoinParams) {
        const initialJoinIntentKey = getExplicitJoinIntentKey({
          sessionName,
          userId,
          jwt,
          rtFromUrl,
          guestToken,
        });
        if (initialJoinIntentKey) {
          lastHandledJoinIntentKeyRef.current = initialJoinIntentKey;
        }
      }

      const handedOff = await requestExistingVideoWindowTakeover();
      if (!cancelled && !handedOff) {
        joinFromQueryParams();
      }
    };

    maybeReuseExistingWindow();

    return () => {
      cancelled = true;
      clearPendingHandoffRequest();
    };
  }, []);

  useEffect(() => {
    lastObservedJoinHrefRef.current = `${window.location.href || ""}`;

    const handleUrlJoinIntentChange = async (source = "unknown") => {
      const currentHref = `${window.location.href || ""}`;
      if (!currentHref) return;
      if (currentHref === lastObservedJoinHrefRef.current) return;
      lastObservedJoinHrefRef.current = currentHref;

      const params = getJoinParams();
      if (!params?.hasExplicitJoinParams) {
        return;
      }

      const nextJoinIntentKey = getExplicitJoinIntentKey(params);
      if (!nextJoinIntentKey) {
        return;
      }
      if (nextJoinIntentKey === lastHandledJoinIntentKeyRef.current) {
        return;
      }
      if (joinIntentChangeInFlightRef.current) {
        return;
      }

      joinIntentChangeInFlightRef.current = true;
      lastHandledJoinIntentKeyRef.current = nextJoinIntentKey;

      const { safeUrl, hasQuery, hasHash } = getSafeUrlForWindowLog(currentHref);
      logVideoWindowEvent("join-intent.url-change.detected", {
        source,
        safeUrl,
        hasQuery,
        hasHash,
      });

      if (isSessionActiveRef.current) {
        logVideoWindowEvent("join-intent.url-change.inplace-switch", {
          source,
          safeUrl,
          hasQuery,
          hasHash,
        });

        try {
          if (sessionContainerRef.current) {
            uitoolkit.closeSession(sessionContainerRef.current);
          }
        } catch (e) {
          // ignore
        }

        try {
          if (
            uitoolkit.client &&
            typeof uitoolkit.client.leave === "function"
          ) {
            await leaveSessionForAllSafe({
              endForAll: false,
              logUnexpected: false,
            });
          }
        } catch (e) {
          // ignore
        }

        await new Promise((resolve) => {
          window.setTimeout(resolve, 80);
        });

        await joinFromQueryParams();
        return;
      }

      try {
        await joinFromQueryParams();
      } finally {
        joinIntentChangeInFlightRef.current = false;
      }
    };

    const handleHashChange = () => {
      void handleUrlJoinIntentChange("hashchange");
    };
    const handlePopState = () => {
      void handleUrlJoinIntentChange("popstate");
    };
    const handleFocus = () => {
      void handleUrlJoinIntentChange("focus");
    };

    window.addEventListener("hashchange", handleHashChange);
    window.addEventListener("popstate", handlePopState);
    window.addEventListener("focus", handleFocus);

    const hrefPollIntervalId = window.setInterval(() => {
      void handleUrlJoinIntentChange("url-poll");
    }, 500);

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("focus", handleFocus);
      window.clearInterval(hrefPollIntervalId);
    };
  }, []);

  useEffect(() => {
    if (!isTopLevelVideoWindowContext()) {
      return undefined;
    }

    const tabId = getOrCreateVideoWindowTabId();
    claimPrimaryVideoWindow("mount");

    const handleHandoffPayload = (payload, source = "unknown") => {
      if (!payload || typeof payload !== "object") {
        return;
      }

      const emittedAt = Number(payload?.emittedAt || 0);
      if (
        Number.isFinite(emittedAt) &&
        emittedAt > 0 &&
        Date.now() - emittedAt > REZRATE_VIDEO_WINDOW_HANDOFF_MESSAGE_TTL_MS
      ) {
        return;
      }

      const nonce = `${payload?.nonce || ""}`.trim();
      if (nonce) {
        if (handoffLastSeenNonceRef.current === nonce) {
          return;
        }
        handoffLastSeenNonceRef.current = nonce;
      }

      handleIncomingVideoWindowHandoffMessage(payload, source);
    };

    const pollLatestHandoffMessage = () => {
      try {
        const raw = window.localStorage.getItem(
          REZRATE_VIDEO_WINDOW_HANDOFF_STORAGE_KEY
        );
        if (!raw) return;
        const parsed = JSON.parse(raw);
        handleHandoffPayload(parsed, "storage-poll");
      } catch (e) {
        // ignore
      }
    };

    try {
      if (typeof window.BroadcastChannel === "function") {
        handoffChannelRef.current = new window.BroadcastChannel(
          REZRATE_VIDEO_WINDOW_HANDOFF_CHANNEL
        );
        handoffChannelRef.current.onmessage = (event) => {
          handleHandoffPayload(event?.data, "broadcast");
        };
      }
    } catch (e) {
      handoffChannelRef.current = null;
    }

    const handleStorage = (event) => {
      if (
        event?.key !== REZRATE_VIDEO_WINDOW_HANDOFF_STORAGE_KEY ||
        !event?.newValue
      ) {
        return;
      }

      try {
        const parsed = JSON.parse(event.newValue);
        handleHandoffPayload(parsed, "storage");
      } catch (e) {
        // ignore
      }
    };

    const handleBeforeUnloadForPrimary = () => {
      clearPrimaryVideoWindowClaim("beforeunload");
    };

    window.addEventListener("storage", handleStorage);
    window.addEventListener("beforeunload", handleBeforeUnloadForPrimary);
    window.addEventListener("pagehide", handleBeforeUnloadForPrimary);

    pollLatestHandoffMessage();
    handoffStoragePollIntervalRef.current = window.setInterval(
      pollLatestHandoffMessage,
      REZRATE_VIDEO_WINDOW_HANDOFF_POLL_INTERVAL_MS
    );

    primaryWindowHeartbeatRef.current = window.setInterval(() => {
      refreshPrimaryVideoWindowHeartbeat();
    }, REZRATE_VIDEO_PRIMARY_WINDOW_HEARTBEAT_MS);

    logVideoWindowEvent("handoff.bootstrap", {
      tabId,
      safePath: getSafePathForWindowLog(),
      hasBroadcastChannel: !!handoffChannelRef.current,
    });

    return () => {
      if (primaryWindowHeartbeatRef.current) {
        window.clearInterval(primaryWindowHeartbeatRef.current);
        primaryWindowHeartbeatRef.current = null;
      }
      if (handoffStoragePollIntervalRef.current) {
        window.clearInterval(handoffStoragePollIntervalRef.current);
        handoffStoragePollIntervalRef.current = null;
      }
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("beforeunload", handleBeforeUnloadForPrimary);
      window.removeEventListener("pagehide", handleBeforeUnloadForPrimary);
      clearPendingHandoffRequest();
      clearPrimaryVideoWindowClaim("cleanup");

      try {
        if (handoffChannelRef.current) {
          handoffChannelRef.current.close();
        }
      } catch (e) {
        // ignore
      } finally {
        handoffChannelRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const handleWindowError = (event) => {
      const err = event?.error || event?.message;
      if (isKnownToolkitSnackbarError(err)) {
        // Suppress a known UI toolkit notistack throw when its message payload is empty.
        suppressGlobalErrorEvent(event);
        return false;
      }
      if (isWakeLockPermissionsPolicyError(err)) {
        suppressGlobalErrorEvent(event);
        return false;
      }
      return undefined;
    };

    const previousOnError = window.onerror;
    window.onerror = function (message, source, lineno, colno, error) {
      if (isKnownToolkitSnackbarError(error || message)) {
        return true;
      }
      if (isWakeLockPermissionsPolicyError(error || message)) {
        return true;
      }
      if (typeof previousOnError === "function") {
        return previousOnError.call(window, message, source, lineno, colno, error);
      }
      return false;
    };

    window.addEventListener("error", handleWindowError, true);
    return () => {
      window.removeEventListener("error", handleWindowError, true);
      window.onerror = previousOnError;
    };
  }, []);

  useEffect(() => {
    const handleUnhandledRejection = (event) => {
      const reason = event?.reason;
      if (isKnownToolkitSnackbarError(reason)) {
        suppressGlobalErrorEvent(event);
        return;
      }
      if (isWakeLockPermissionsPolicyError(reason)) {
        suppressGlobalErrorEvent(event);
        return;
      }
      if (isExpectedSessionTerminationError(reason)) {
        suppressGlobalErrorEvent(event);
        return;
      }
      if (isVirtualBackgroundFailure(reason)) {
        // Virtual background failures should not block the meeting; disable blur and continue.
        try {
          const mediaKey =
            mediaPrefsStorageKeyRef.current ||
            computeMediaPrefsStorageKey(joinConfigRef.current || {});
          mediaPrefsStorageKeyRef.current = mediaKey;
          const nextPrefs = { ...(mediaPrefsRef.current || {}) };
          nextPrefs.blurBackground = false;
          mediaPrefsRef.current = nextPrefs;
          writeMediaPrefs(mediaKey, nextPrefs);
          applyBlurBackgroundPreferenceInToolkitUi();
        } catch (e) {
          // ignore
        }
        suppressGlobalErrorEvent(event);
        return;
      }
      const msg = formatErrorMessage(reason, "Unexpected meeting error");
      console.error("Unhandled rejection:", msg);
      setError(msg);
      suppressGlobalErrorEvent(event);
    };

    window.addEventListener("unhandledrejection", handleUnhandledRejection, true);
    return () => {
      window.removeEventListener("unhandledrejection", handleUnhandledRejection, true);
    };
  }, []);

  useEffect(() => {
    // Poll for end time changes when we have an exchange token (#m) (candidate and guest flows).
    // This allows admin schedule edits to propagate without requiring the user to re-open the meeting.
    if (!isSessionActive) return;

    const { rt } = getJoinParams();
    if (!rt) return;

    let isCancelled = false;
    const interval = setInterval(async () => {
      if (isCancelled) return;
      try {
        const refreshedConfig = await fetchJoinConfig({
          refreshUrl: null,
          sessionName: null,
          rt,
        });
        if (!refreshedConfig) return;

        if (Number.isFinite(Number(refreshedConfig?.serverNow))) {
          setServerOffsetFromServerNowSeconds(Number(refreshedConfig.serverNow));
        }

        const currentEndAt = Number(
          getEndAtFromConfig(joinConfigRef.current || {})
        );
        const newEndAt = Number(getEndAtFromConfig(refreshedConfig));

        if (
          Number.isFinite(newEndAt) &&
          newEndAt > 0 &&
          (!Number.isFinite(currentEndAt) || newEndAt !== currentEndAt)
        ) {
          joinConfigRef.current = {
            ...(joinConfigRef.current || {}),
            ...refreshedConfig,
          };
          scheduleEndAt(newEndAt);
        }
      } catch (e) {
        // ignore
      }
    }, 60 * 1000);

    return () => {
      isCancelled = true;
      clearInterval(interval);
    };
  }, [isSessionActive]);

  useEffect(() => {
    // Secondary enforcement: even if endAt drifted or the user never refreshed,
    // poll the API to see whether the meeting should be ended now.
    if (!isSessionActive) return;

    endedConsecutiveRef.current = 0;
    let isCancelled = false;
    const interval = setInterval(async () => {
      if (isCancelled) return;
      try {
        const status = await fetchEmbeddedStatus();
        if (!status) return;

        if (Number.isFinite(Number(status?.serverNow))) {
          setServerOffsetFromServerNowSeconds(Number(status.serverNow));
        }

        if (Number.isFinite(Number(status?.endAt)) && Number(status.endAt) > 0) {
          const currentEndAt = Number(getEndAtFromConfig(joinConfigRef.current || {}));
          const newEndAt = Number(status.endAt);
          if (!Number.isFinite(currentEndAt) || currentEndAt !== newEndAt) {
            joinConfigRef.current = {
              ...(joinConfigRef.current || {}),
              endAt: newEndAt,
            };
            scheduleEndAt(newEndAt);
          }
        }

        if (status.ended) {
          // Guard against transient status responses (e.g., brief DB blips) ending an active meeting.
          endedConsecutiveRef.current += 1;
          if (endedConsecutiveRef.current < 2) return;
          if (sessionContainerRef.current) {
            try {
              uitoolkit.closeSession(sessionContainerRef.current);
            } catch (e) {}
          }
          handleSessionEnded();
        } else {
          endedConsecutiveRef.current = 0;
        }
      } catch (e) {
        // ignore
      }
    }, 30 * 1000);

    return () => {
      isCancelled = true;
      clearInterval(interval);
      endedConsecutiveRef.current = 0;
    };
  }, [isSessionActive]);

  return (
    <div className="App">
      <main
        style={{ height: "100vh", display: "flex", flexDirection: "column" }}
      >
        {isSessionActive && (
          <>
            <div
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                background: "#4caf50",
                color: "white",
                padding: "5px 10px",
                borderRadius: "4px",
                zIndex: 1000,
              }}
            >
              Session Active
              {typeof endsInSeconds === "number"
                ? ` | Ends in: ${formatEndsIn(endsInSeconds)}`
                : ""}
              {INACTIVITY_TIMEOUT ? ` | Timeout in: ${timeRemaining}s` : ""}
            </div>

            {!!displayTopic && (
              <div
                style={{
                  position: "absolute",
                  top: "10px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  background: "rgba(0, 0, 0, 0.7)",
                  color: "white",
                  padding: "6px 12px",
                  borderRadius: "6px",
                  zIndex: 1000,
                  maxWidth: "60vw",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontWeight: 600,
                }}
                title={displayTopic}
              >
                {displayTopic}
              </div>
            )}
            
            {/* End Session button only visible to hosts */}
            {isHost && (
              <div
                style={{
                  position: "absolute",
                  top: "10px",
                  left: "10px",
                  zIndex: 1000,
                }}
              >
                <button
                  onClick={endSessionForAll}
                  style={{
                    background: "#e02020",
                    color: "white",
                    border: "none",
                    padding: "8px 16px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontWeight: "bold",
                  }}
                >
                  End Session For All
                </button>
              </div>
            )}
          </>
        )}

        {/* Session Ended Popup */}
        {showSessionEndedPopup && (
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              background: "white",
              border: "1px solid #ccc",
              borderRadius: "8px",
              padding: "20px",
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
              zIndex: 2000,
              textAlign: "center",
              minWidth: "300px",
            }}
          >
            <h2 style={{ margin: "0 0 15px 0", color: "#e02020" }}>
              Session Ended
            </h2>
            <p style={{ margin: "0 0 20px 0" }}>
              Your session has ended due to inactivity or was closed by the
              host.
            </p>
            <button
              style={{
                background: "#0056b3",
                color: "white",
                border: "none",
                padding: "8px 16px",
                borderRadius: "4px",
                cursor: "pointer",
              }}
              onClick={() => setShowSessionEndedPopup(false)}
            >
              Close
            </button>
          </div>
        )}

        <div
          id="join-flow"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            minHeight: 0,
            textAlign: "center",
            padding: "24px",
            boxSizing: "border-box",
            background: "#ffffff",
            overflow: "hidden",
          }}
        >
          <img
            src="/rezRateLogo.png"
            alt="RezRate Logo"
            style={{
              width: "200px",
              marginBottom: "20px",
              // Some Zoom SDK stylesheets apply global `img { float: left; }`,
              // which breaks our centered join/error screen layout.
              float: "none",
              display: "block",
              alignSelf: "center",
            }}
          />
          <h1>RezRate Meeting Service</h1>

          {error && (
            <div style={{ color: "red", margin: "10px 0" }}>Error: {error}</div>
          )}
          {handoffNotice && (
            <div style={{ color: "#0056b3", margin: "10px 0" }}>
              {handoffNotice}
            </div>
          )}
        </div>
        <div id="sessionContainer" style={{ width: "100%", height: "100%" }}></div>
      </main>
    </div>
  );
}

function App() {
  const normalizedPath = `${window.location.pathname || "/"}`
    .replace(/\/+$/, "") || "/";

  if (normalizedPath === "/preview") {
    return <PreviewPage />;
  }

  return <MeetingApp />;
}

export default App;
